import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { todayStr, SOURCE_COLOR, STATUS_COLOR, formatDate } from '../lib/api';
import { undoableAction } from '../lib/undoable';
import { ReservationDialog, NewReservationDialog } from '../components/ReservationDialogs';
import DateStepper from '../components/DateStepper';
import { UserPlus, Eye, EyeOff, Plus, GripVertical, Check, UserCheck, LayoutGrid, Map, Pencil, Save, X, Lock, Unlock, Maximize2, Search, Users, Link2, Clock, Wine, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, useDraggable, useDroppable,
  PointerSensor, TouchSensor, MouseSensor, useSensor, useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

const STATE_COLOR = {
  free: '#22C55E',
  reserved: '#3B82F6',
  seated: '#F97316',
  blocked: '#EF4444',
  cleaning: '#9CA3AF',
};

const STATE_LABEL = {
  free: 'Frei',
  reserved: 'Reserviert',
  seated: 'Sitzt',
  blocked: 'Gesperrt',
  cleaning: 'Reinigung',
};

// Lila-Markierung für Tischgruppen (Kombinationen), die über mehrere Bereiche gehen
const CROSS_AREA_COLOR = '#9333EA';
// Türkis-Markierung für Tische mit aktivem Smart-Tischsplitting, die belegt sind
const SPLIT_TILE_COLOR = '#14B8A6';
function comboSpansMultipleAreas(comboId, combinations, tables) {
  if (!comboId) return false;
  const combo = (combinations || []).find(c => c.id === comboId);
  if (!combo) return false;
  const areaIds = new Set(
    (combo.table_ids || [])
      .map(tid => (tables || []).find(t => t.id === tid)?.area_id)
      .filter(Boolean)
  );
  return areaIds.size >= 2;
}

function hmToMin(hm) {
  if (!hm || typeof hm !== 'string' || !hm.includes(':')) return null;
  const [h, m] = hm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// Aktuelle Uhrzeit in Restaurant-Zeit (Europe/Berlin) in Minuten seit Mitternacht —
// unabhängig von der Geräte-Zeitzone, damit der Aufenthalts-Ring überall korrekt ist.
function nowMinBerlin() {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const h = +parts.find(p => p.type === 'hour').value;
    const m = +parts.find(p => p.type === 'minute').value;
    return h * 60 + m;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
}

// Kreisförmiger Aufenthalts-Timer: voller Ring = gerade gesetzt, leer = Zeit erreicht,
// roter Ring = Aufenthaltszeit überzogen. Mitte zeigt verbleibende Minuten (Text-Timer).
function StayRing({ startMin, endMin, nowMin, size = 30, stroke = 3.5 }) {
  if (startMin == null || endMin == null || nowMin == null || endMin <= startMin) return null;
  const total = endMin - startMin;
  const remaining = endMin - nowMin;
  const overdrawn = remaining < 0;
  const frac = Math.max(0, Math.min(1, remaining / total));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const color = overdrawn ? '#EF4444' : (frac <= 0.25 ? '#F59E0B' : '#22C55E');
  const offset = overdrawn ? 0 : c * (1 - frac);
  const label = overdrawn ? `+${Math.abs(remaining)}` : `${remaining}`;
  return (
    <span
      data-testid="fp-stay-ring"
      title={overdrawn ? `Aufenthalt um ${Math.abs(remaining)} Min überzogen` : `noch ${remaining} Min`}
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="block" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E5E0" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.4s ease' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono leading-none tabular-nums"
        style={{ fontSize: 8, color }}>
        {label}
      </span>
    </span>
  );
}

function tableBaseStatus(t, dayResvs) {
  if (t.status_override === 'blocked') return 'blocked';
  if (t.status_override === 'cleaning') return 'cleaning';
  if (!t.active) return 'blocked';
  const seated = dayResvs.find(r => ['seated', 'arrived'].includes(r.status));
  if (seated) return 'seated';
  const reserved = dayResvs.find(r => ['confirmed', 'pending'].includes(r.status));
  if (reserved) return 'reserved';
  return 'free';
}

// Smart-Tischsplitting: Restkapazität eines teilbaren Tisches (Spiegel von backend table_split_occupancy).
// atMin != null → Belegung zu diesem Zeitpunkt; atMin == null → Tages-Spitzenbelegung.
const SPLIT_ACTIVE_STATUSES = ['pending', 'confirmed', 'arrived', 'seated'];
function splitOccupancy(table, dayResvs, atMin) {
  const cap = table.max_persons || 0;
  const active = (dayResvs || []).filter(r => SPLIT_ACTIVE_STATUSES.includes(r.status));
  const activeAt = (p) => active.filter(r => {
    const s = hmToMin(r.start_time), e = hmToMin(r.end_time);
    return s != null && e != null && s <= p && p < e;
  });
  let occupied = 0;
  let parts = [];
  if (atMin != null) {
    parts = activeAt(atMin);
    occupied = parts.reduce((a, r) => a + (r.persons || 0), 0);
  } else {
    const points = new Set();
    active.forEach(r => { const s = hmToMin(r.start_time); if (s != null) points.add(s); });
    [...points].sort((a, b) => a - b).forEach(p => {
      const cur = activeAt(p);
      const sum = cur.reduce((a, r) => a + (r.persons || 0), 0);
      if (sum > occupied) { occupied = sum; parts = cur; }
    });
  }
  const free = Math.max(0, cap - occupied);
  const status = occupied === 0 ? 'free' : (free === 0 ? 'full' : 'partial');
  return { cap, occupied, free, status, parts, count: parts.length };
}

export default function AdminFloorPlan() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialDate = searchParams.get('date') || todayStr();
  const [date, setDate] = useState(initialDate);
  const [highlightTableId, setHighlightTableId] = useState(searchParams.get('highlight') || null);
  const [openResvId, setOpenResvId] = useState(searchParams.get('resv') || null);
  const [openPopoverTableId, setOpenPopoverTableId] = useState(searchParams.get('popover') || null);
  const [areas, setAreas] = useState([]);
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [hiddenAreas, setHiddenAreas] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [splitMode, setSplitMode] = useState(false);
  const [defaultTable, setDefaultTable] = useState(null);
  const [activeDragResv, setActiveDragResv] = useState(null);
  const [swapPrompt, setSwapPrompt] = useState(null);  // { sourceResv, targetResv, sourceTable, targetTable } or null
  const [filterMode, setFilterMode] = useState('active');  // 'all' | 'active' | 'occupied' | 'free' | 'cleaning'
  const [viewMode, setViewMode] = useState('plan');  // 'plan' (Haupt) | 'grid'
  const [waiters, setWaiters] = useState([]);
  const [dayBlocks, setDayBlocks] = useState([]);
  const [areaClosures, setAreaClosures] = useState([]);
  const [dayBlockTable, setDayBlockTable] = useState(null);
  const [search, setSearch] = useState('');
  const [tagsCatalog, setTagsCatalog] = useState([]);
  const [waitlist, setWaitlist] = useState([]);

  // Reservations matching the search query (name/phone/email) for the current day
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return reservations
      .filter(r => !['completed', 'cancelled', 'declined', 'no_show'].includes(r.status))
      .filter(r =>
        (r.customer_name || '').toLowerCase().includes(q)
        || (r.phone || '').toLowerCase().includes(q)
        || (r.email || '').toLowerCase().includes(q)
      )
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .slice(0, 8);
  }, [search, reservations]);

  // Reservations for the selected date — desktop-only right-side list in the floor plan
  const dayResvList = useMemo(() => {
    return reservations
      .filter(r => !['cancelled', 'declined', 'no_show'].includes(r.status))
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [reservations]);

  // Open waitlist entries across all days (guests still waiting)
  const activeWaitlist = useMemo(() => {
    return (waitlist || []).filter(w => ['new', 'waiting', 'offered', 'confirmed'].includes(w.status));
  }, [waitlist]);

  // Resolve tag objects (name + color) for a reservation
  const tagsForResv = (r) => {
    const ids = r.tag_ids || [];
    if (!ids.length || !tagsCatalog.length) return [];
    return ids.map(tid => tagsCatalog.find(t => t.id === tid)).filter(Boolean);
  };

  const tableNumbersFor = (r) => {
    if (r.assigned_combination_id) {
      const c = combinations.find(c => c.id === r.assigned_combination_id);
      if (c) return (c.table_ids || []).map(tid => tables.find(t => t.id === tid)?.table_number).filter(Boolean).join('+');
    }
    const t = tables.find(t => t.id === r.assigned_table_id);
    return t ? t.table_number : '–';
  };

  const onSearchSelect = (r) => {
    setSelected(r);
    const tid = r.assigned_combination_id
      ? (combinations.find(c => c.id === r.assigned_combination_id)?.table_ids || [])[0]
      : r.assigned_table_id;
    if (tid) setHighlightTableId(tid);
    setSearch('');
  };

  // When arriving with ?popover=<id>, make sure the Grundriss (plan) view is active.
  useEffect(() => {
    if (openPopoverTableId) setViewMode('plan');
  }, [openPopoverTableId]);

  // Consume the ?popover=<tableId> param once FloorPlanView has opened it.
  const onPopoverConsumed = useCallback(() => {
    setOpenPopoverTableId(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('popover');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Split a table-combination reservation down to a single kept table; the other
  // table(s) of the combo become free. Backend re-checks availability (excl. this resv).
  const reduceCombination = async (resvId, keepTableIds) => {
    const keep = Array.isArray(keepTableIds) ? keepTableIds : [keepTableIds];
    const keepNums = keep.map(id => tables.find(t => t.id === id)?.table_number).filter(Boolean).join(' + ');
    try {
      await api.put(`/reservations/${resvId}`, { table_ids: keep });
      toast.success(keep.length > 1
        ? `Kombination reduziert – Tische ${keepNums} behalten, übrige frei.`
        : `Kombination aufgelöst – nur Tisch ${keepNums} behalten, übrige frei.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tisch konnte nicht freigegeben werden (Konflikt).');
    }
  };

  // Auto-clear highlight after a few seconds and remove from URL
  useEffect(() => {
    if (!highlightTableId) return;
    const tid = highlightTableId;
    // Ensure the table's area is visible
    const t = tables.find(x => x.id === tid);
    if (t && hiddenAreas.has(t.area_id)) {
      setHiddenAreas(prev => {
        const next = new Set(prev);
        next.delete(t.area_id);
        return next;
      });
    }
    // Scroll into view shortly after render
    const scrollT = setTimeout(() => {
      const el = document.querySelector(`[data-testid="fp-table-${tid}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 350);
    // Clear highlight after 4 s
    const clearT = setTimeout(() => {
      setHighlightTableId(null);
      const next = new URLSearchParams(searchParams);
      next.delete('highlight');
      setSearchParams(next, { replace: true });
    }, 4000);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTableId, tables.length]);

  // Open a specific reservation directly (e.g. clicked from a notification): ?resv=<id>
  useEffect(() => {
    if (!openResvId) return;
    if (reservations.length === 0) return;  // wait for the day's data to load
    const resv = reservations.find(r => r.id === openResvId);
    if (resv) {
      setSelected(resv);
      let tid = resv.assigned_table_id;
      if (!tid && resv.assigned_combination_id) {
        const combo = (combinations || []).find(c => c.id === resv.assigned_combination_id);
        tid = combo && combo.table_ids && combo.table_ids[0];
      }
      if (tid) setHighlightTableId(tid);
    } else {
      toast.info('Reservierung nicht an diesem Tag gefunden');
    }
    setOpenResvId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('resv');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openResvId, reservations, combinations]);

  // Sync URL params → state when navigating here while already mounted
  // (e.g. clicking a reservation in the desktop sidebar list).
  useEffect(() => {
    const d = searchParams.get('date');
    if (d) setDate(d);
    const rv = searchParams.get('resv');
    if (rv) setOpenResvId(rv);
    const pv = searchParams.get('popover');
    if (pv) setOpenPopoverTableId(pv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);


  const load = useCallback(async () => {
    const [a, t, r, cb, w, db, tg, wl, adc] = await Promise.all([
      api.get('/areas'),
      api.get('/tables'),
      api.get(`/reservations?date=${date}`),
      api.get('/table-combinations/all'),
      api.get('/waiters'),
      api.get(`/table-day-blocks?date=${date}`),
      api.get('/tags/public').catch(() => ({ data: [] })),
      api.get(`/waitlist?active_only=true`).catch(() => ({ data: [] })),
      api.get(`/area-day-closures?date_from=${date}&date_to=${date}`).catch(() => ({ data: [] })),
    ]);
    setAreas(a.data);
    setTables(t.data);
    setReservations(r.data.filter(x => x.status !== 'completed'));
    setCombinations(cb.data || []);
    setWaiters(w.data || []);
    setDayBlocks(db.data || []);
    setTagsCatalog(tg.data || []);
    setWaitlist(wl.data || []);
    setAreaClosures(adc.data || []);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so new customer reservations appear without manual reload
  useEffect(() => {
    const t = setInterval(() => { load(); }, 30000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  // dnd-kit sensors: support both pointer (mouse/touch) with delay to avoid conflict with click
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const toggleArea = (id) => {
    const ns = new Set(hiddenAreas);
    ns.has(id) ? ns.delete(id) : ns.add(id);
    setHiddenAreas(ns);
  };

  const setTableStatus = async (tableId, status_override) => {
    try {
      await api.put(`/tables/${tableId}/status`, { status_override });
      toast.success(
        status_override === 'blocked' ? 'Tisch gesperrt' :
        status_override === 'cleaning' ? 'Reinigung markiert' :
        'Status zurückgesetzt'
      );
      load();
    } catch {
      toast.error('Fehler');
    }
  };

  const openNewForTable = (table, walkIn, share = false) => {
    setDefaultTable(table);
    setSplitMode(!!share);
    if (walkIn) setShowWalkIn(true);
    else setShowNew(true);
  };

  // Quick 1-click walk-in: seats the default party (2, clamped to table capacity)
  // immediately on a FREE table, no dialog. The backend computes a duration that
  // fits before the next reservation (incl. buffer) and rejects any collision —
  // so it never clashes with a manual reservation. Works regardless of opening
  // hours. Falls back to the full dialog only if the table is not free now.
  const quickWalkIn = async (table) => {
    if (table.status_override === 'blocked') { toast.error('Tisch ist gesperrt'); return; }
    try {
      const now = new Date();
      const start_time = `${String(now.getHours()).padStart(2, '0')}:${String(Math.floor(now.getMinutes() / 5) * 5).padStart(2, '0')}`;
      const r = await api.post('/reservations/quick-walkin', { table_id: table.id, date, start_time });
      const resv = r.data || {};
      toast.success(`Walk-in · Tisch ${table.table_number} · ${resv.persons || 2} Pers.${resv.end_time ? ` · bis ${resv.end_time}` : ''}`);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(e.response?.data?.detail || 'Tisch gerade nicht frei – bitte Details prüfen.');
        openNewForTable(table, true);
      } else {
        toast.error(e.response?.data?.detail || 'Walk-in fehlgeschlagen');
      }
    }
  };

  const releaseTable = async (resv) => {
    const prevEnd = resv.end_time;
    const prevStatus = resv.status;
    try {
      await api.post(`/reservations/${resv.id}/release-table`);
      undoableAction({
        label: `Tisch freigegeben (${resv.customer_name})`,
        description: `Ende zurück auf ${prevEnd}?`,
        onUndo: () => api.put(`/reservations/${resv.id}`, { end_time: prevEnd, status: prevStatus }),
        onUndone: load,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Freigeben');
    }
  };

  const seatGuest = async (resv) => {
    const prevStatus = resv.status;
    try {
      await api.post(`/reservations/${resv.id}/status`, { status: 'seated' });
      undoableAction({
        label: `${resv.customer_name} sitzt`,
        description: `Zurück zu „${prevStatus}"?`,
        onUndo: () => api.post(`/reservations/${resv.id}/status`, { status: prevStatus }),
        onUndone: load,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleDragStart = (event) => {
    const resvId = event.active.id;
    const resv = reservations.find(r => r.id === resvId);
    setActiveDragResv(resv);
  };

  const handleDragEnd = (event) => {
    const resvId = event.active.id;
    const targetTableId = event.over?.id;
    setActiveDragResv(null);
    applyReservationDrop(resvId, targetTableId);
  };

  const confirmSwap = async () => {
    if (!swapPrompt) return;
    const { sourceResv, targetResv } = swapPrompt;
    try {
      await api.post('/reservations/swap-tables', {
        reservation_id_a: sourceResv.id,
        reservation_id_b: targetResv.id,
      });
      toast.success('Tische getauscht');
      setSwapPrompt(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tausch nicht möglich');
    }
  };

  // Day-block maps (per current date)
  const dayBlocksByTable = useMemo(() => {
    const m = {};
    (dayBlocks || []).forEach(b => { (m[b.table_id] = m[b.table_id] || []).push(b); });
    return m;
  }, [dayBlocks]);
  const fullDayBlockedSet = useMemo(
    () => new Set((dayBlocks || []).filter(b => b.full_day || !b.start_time || !b.end_time).map(b => b.table_id)),
    [dayBlocks]
  );
  // Areas closed for the whole day (Bereichs-Tagessperre ohne Zeitfenster) → deren Tische gelten als gesperrt.
  const fullDayClosedAreaSet = useMemo(
    () => new Set((areaClosures || []).filter(c => !c.start_time || !c.end_time).map(c => c.area_id)),
    [areaClosures]
  );

  // Shared move/swap logic when a reservation is dropped onto a table — used by
  // both the grid drag-and-drop and the floor-plan reservation drag.
  // Rules: capacity too small → blocked; target free → move; target occupied → swap dialog.
  const applyReservationDrop = (resvId, targetTableId) => {
    if (!resvId || !targetTableId) return;
    const resv = reservations.find(r => r.id === resvId);
    if (!resv || resv.assigned_table_id === targetTableId) return;
    const targetTable = tables.find(t => t.id === targetTableId);
    if (!targetTable) return;
    if (fullDayBlockedSet.has(targetTableId) || fullDayClosedAreaSet.has(targetTable.area_id)) {
      toast.error(`Tisch ${targetTable.table_number} ist an diesem Tag gesperrt.`);
      return;
    }
    const isCombo = (r) => !!r.assigned_combination_id;
    // Capacity guard (single-table reservations only): a 4-person reservation
    // cannot move onto a 2-person table.
    if (!isCombo(resv) && resv.persons > (targetTable.max_persons || 0)) {
      toast.error(`Tisch ${targetTable.table_number} ist zu klein (max. ${targetTable.max_persons} Pers.) für ${resv.persons} Pers. — Tausch nicht möglich.`);
      return;
    }
    const toMin = (hm) => { const [h, m] = (hm || '0:0').split(':').map(Number); return h * 60 + m; };
    const sStart = toMin(resv.start_time);
    const sEnd = toMin(resv.end_time);
    // Overlapping single-table reservation already on the target table?
    const targetResv = reservations.find(r =>
      r.id !== resv.id &&
      r.assigned_table_id === targetTableId &&
      !isCombo(r) &&
      !['cancelled', 'declined', 'no_show', 'completed'].includes(r.status) &&
      toMin(r.start_time) < sEnd && toMin(r.end_time) > sStart
    );
    if (targetResv && !isCombo(resv)) {
      // Teilbarer Tisch → direkt mitsetzen (Co-Seating). Reichen die Restplätze nicht
      // (Backend lehnt mit 409 ab), fällt es wie bisher auf den Tausch-Dialog zurück.
      if (targetTable.split_enabled) {
        (async () => {
          try {
            await api.put(`/reservations/${resvId}`, { assigned_table_id: targetTableId });
            toast.success(`Auf gesplitteten Tisch ${targetTable.table_number} mitgesetzt`);
            load();
          } catch (e) {
            if (e.response?.status === 409) {
              // Nicht genug Restplätze zum Mitsetzen → wie bisher Tausch-Dialog anbieten
              setSwapPrompt({
                sourceResv: resv,
                targetResv,
                sourceTable: tables.find(t => t.id === resv.assigned_table_id),
                targetTable,
              });
            } else {
              toast.error(e.response?.data?.detail || 'Mitsetzen nicht möglich');
            }
          }
        })();
        return;
      }
      // Target occupied → open swap confirmation dialog
      setSwapPrompt({
        sourceResv: resv,
        targetResv,
        sourceTable: tables.find(t => t.id === resv.assigned_table_id),
        targetTable,
      });
      return;
    }
    // Target free → simple move
    (async () => {
      try {
        await api.put(`/reservations/${resvId}`, { assigned_table_id: targetTableId });
        toast.success('Reservierung verschoben');
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Verschieben nicht möglich');
      }
    })();
  };

  // Drag a reservation FROM the right-hand list onto a table in the floor plan.
  // Reuses applyReservationDrop (free→move, occupied→swap, too small→blocked).
  // Pointer-based so a small move counts as a drag, a plain click still opens.
  const [listDrag, setListDrag] = useState(null);  // { resv, x, y } while dragging from the list
  const onListResvPointerDown = (e, resv) => {
    if (e.button != null && e.button !== 0) return;  // left button / touch only
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;
    let lastNode = null;
    const clearHighlight = () => {
      if (lastNode) { lastNode.style.outline = ''; lastNode.style.outlineOffset = ''; }
      lastNode = null;
    };
    const move = (ev) => {
      if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) > 8) moved = true;
      if (!moved) return;
      setListDrag({ resv, x: ev.clientX, y: ev.clientY });
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const node = el && el.closest && el.closest('[data-testid^="fp-plan-table-"]');
      if (node !== lastNode) {
        clearHighlight();
        if (node) { node.style.outline = '3px solid #22C55E'; node.style.outlineOffset = '2px'; lastNode = node; }
      }
    };
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      clearHighlight();
      setListDrag(null);
      if (!moved) { onSearchSelect(resv); return; }  // treated as a click
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const node = el && el.closest && el.closest('[data-testid^="fp-plan-table-"]');
      if (node) {
        const tid = node.getAttribute('data-testid').replace('fp-plan-table-', '');
        applyReservationDrop(resv.id, tid);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const getTableData = useCallback((t) => {
    const tableComboIds = new Set(
      (combinations || [])
        .filter(c => (c.table_ids || []).includes(t.id))
        .map(c => c.id)
    );
    const dayResvs = reservations
      .filter(r =>
        !['cancelled', 'declined', 'no_show'].includes(r.status) &&
        (r.assigned_table_id === t.id || (r.assigned_combination_id && tableComboIds.has(r.assigned_combination_id)))
      )
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
    const state = (fullDayBlockedSet.has(t.id) || fullDayClosedAreaSet.has(t.area_id)) ? 'blocked' : tableBaseStatus(t, dayResvs);
    return { dayResvs, state };
  }, [combinations, reservations, fullDayBlockedSet, fullDayClosedAreaSet]);

  const saveLayout = async (positions) => {
    try {
      await api.put('/tables/layout', { positions });
      toast.success('Grundriss gespeichert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const reloadWaiters = async () => {
    try { const w = await api.get('/waiters'); setWaiters(w.data || []); } catch { /* noop */ }
  };
  const assignWaiter = async (tableId, waiterId) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, waiter_id: waiterId } : t));
    try {
      await api.put(`/tables/${tableId}/waiter`, { waiter_id: waiterId });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuweisung fehlgeschlagen');
      load();
    }
  };
  const createWaiter = async (name, color) => {
    try { await api.post('/waiters', { name, color }); toast.success('Kellner angelegt'); reloadWaiters(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const updateWaiter = async (id, patch) => {
    try { await api.put(`/waiters/${id}`, patch); reloadWaiters(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const deleteWaiter = async (id) => {
    try { await api.delete(`/waiters/${id}`); toast.success('Kellner gelöscht'); reloadWaiters(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const reloadDayBlocks = async () => {
    try { const r = await api.get(`/table-day-blocks?date=${date}`); setDayBlocks(r.data || []); } catch { /* noop */ }
  };
  const createDayBlock = async (payload) => {
    try { await api.post('/table-day-blocks', { ...payload, date }); toast.success('Tisch für diesen Tag gesperrt'); reloadDayBlocks(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Sperren fehlgeschlagen'); }
  };
  const deleteDayBlock = async (id) => {
    try { await api.delete(`/table-day-blocks/${id}`); toast.success('Sperre aufgehoben'); reloadDayBlocks(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1700px]" data-testid="admin-floorplan-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Tischplan</p>
          <h1 className="font-display text-3xl text-[#1C1C19]" data-testid="floorplan-date-label">{formatDate(date)}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative" data-testid="fp-search-wrap">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#5C5C59]" />
            <input
              type="text"
              placeholder="Gast suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="fp-search-input"
              className="w-40 sm:w-52 pl-8 pr-7 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
            />
            {search && (
              <button onClick={() => setSearch('')} data-testid="fp-search-clear" className="absolute right-2 top-2.5 text-[#5C5C59] hover:text-[#1C1C19]">
                <X className="w-4 h-4" />
              </button>
            )}
            {search.trim() && (
              <div className="absolute z-30 mt-1 left-0 w-72 max-w-[80vw] bg-white border border-[#E5E5E0] rounded-lg shadow-xl overflow-hidden" data-testid="fp-search-results">
                {searchResults.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-[#5C5C59]" data-testid="fp-search-empty">Keine Treffer an diesem Tag.</div>
                ) : searchResults.map(r => (
                  <button
                    key={r.id}
                    onClick={() => onSearchSelect(r)}
                    data-testid={`fp-search-result-${r.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F9F9F7] border-b border-[#E5E5E0] last:border-0 flex items-center gap-2"
                  >
                    <span className="font-mono text-xs text-[#1C1C19] w-12 shrink-0">{r.start_time}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-[#1C1C19] truncate">{r.customer_name}</span>
                      <span className="block text-[11px] text-[#5C5C59] truncate">{r.persons}P · Tisch {tableNumbersFor(r)}{r.phone ? ` · ${r.phone}` : ''}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DateStepper value={date} onChange={setDate} testIdPrefix="floorplan-date" />
          <button onClick={() => { setDefaultTable(null); setShowWalkIn(true); }} data-testid="fp-walkin-btn" className="px-3 py-2 border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white rounded text-sm flex items-center gap-1 transition">
            <UserPlus className="w-4 h-4" /> Walk-in
          </button>
          <button onClick={() => { setDefaultTable(null); setShowNew(true); }} data-testid="fp-new-btn" className="px-3 py-2 bg-[#1C1C19] hover:bg-[#3A3A35] text-white rounded text-sm flex items-center gap-1 transition">
            <Plus className="w-4 h-4" /> Reservierung
          </button>
        </div>
      </header>

      <div className="flex gap-6 items-start">
        <div className="flex-1 min-w-0">

      <div className="flex items-center gap-1.5 mb-4" data-testid="fp-view-toggle">
        {[
          { v: 'plan', l: 'Grundriss', icon: <Map className="w-3.5 h-3.5" /> },
          { v: 'grid', l: 'Raster', icon: <LayoutGrid className="w-3.5 h-3.5" /> },
        ].map(opt => (
          <button
            key={opt.v}
            onClick={() => setViewMode(opt.v)}
            data-testid={`fp-view-${opt.v}`}
            className={`px-3 py-1.5 rounded-md text-xs border flex items-center gap-1.5 transition ${
              viewMode === opt.v
                ? 'bg-[#1C1C19] text-white border-[#1C1C19]'
                : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
            }`}
          >
            {opt.icon} {opt.l}
          </button>
        ))}
      </div>

      {viewMode === 'plan' && (
        <FloorPlanView
          areas={areas}
          tables={tables}
          hiddenAreas={hiddenAreas}
          toggleArea={toggleArea}
          getTableData={getTableData}
          onSelectResv={setSelected}
          onReleaseTable={releaseTable}
          onSeatGuest={seatGuest}
          onAddReservation={(t) => openNewForTable(t, false)}
          onAddWalkIn={(t) => openNewForTable(t, true)}
          onQuickWalkIn={(t) => quickWalkIn(t)}
          onSplitWalkIn={(t) => openNewForTable(t, true, true)}
          onSaveLayout={saveLayout}
          highlightTableId={highlightTableId}
          waiters={waiters}
          onAssignWaiter={assignWaiter}
          onCreateWaiter={createWaiter}
          onUpdateWaiter={updateWaiter}
          onDeleteWaiter={deleteWaiter}
          dayBlocksByTable={dayBlocksByTable}
          onOpenDayBlock={(t) => setDayBlockTable(t)}
          isToday={date === todayStr()}
          openPopoverTableId={openPopoverTableId}
          onPopoverConsumed={onPopoverConsumed}
          combinations={combinations}
          onReduceCombination={reduceCombination}
          onMoveReservation={applyReservationDrop}
          closedAreaIds={fullDayClosedAreaSet}
        />
      )}

      {viewMode === 'grid' && (<>
      <div className="flex flex-wrap gap-1 mb-3" data-testid="fp-filter-chips">
        {[
          { v: 'all', l: 'Alle' },
          { v: 'active', l: 'Aktiv' },
          { v: 'occupied', l: 'Belegt' },
          { v: 'free', l: 'Frei' },
          { v: 'cleaning', l: 'Reinigung' },
        ].map(opt => (
          <button
            key={opt.v}
            onClick={() => setFilterMode(opt.v)}
            data-testid={`fp-filter-${opt.v}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition ${
              filterMode === opt.v
                ? 'bg-[#1C1C19] text-white border-[#1C1C19]'
                : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>
      <p className="text-xs text-[#5C5C59] mb-4">
        Tippen = Reservierung öffnen · Lang drücken auf Tisch (0.6 s) = Reinigung · Sehr lang (2 s) = Sperren · Reservierung am Griff ziehen = auf anderen Tisch verschieben · Belegter Tisch = Tausch-Dialog
      </p>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} modifiers={[restrictToWindowEdges]}>
        <div className="space-y-6">
          {areas.length === 0 && (
            <div className="text-center py-12 bg-white border border-dashed border-[#E5E5E0] rounded-lg" data-testid="fp-empty-areas">
              <p className="text-[#5C5C59]">Noch keine Bereiche angelegt. Bitte zuerst Bereiche und Tische erstellen.</p>
            </div>
          )}
          {areas.map(area => {
            const areaTables = tables.filter(t => t.area_id === area.id);
            const isHidden = hiddenAreas.has(area.id);
            return (
              <div key={area.id} className="bg-white border border-[#E5E5E0] rounded-lg" data-testid={`fp-area-${area.id}`}>
                <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: area.color }}></span>
                    <h2 className="font-display text-lg text-[#1C1C19]">{area.name}</h2>
                    <span className="text-xs text-[#5C5C59]">({areaTables.length} Tische)</span>
                    {!area.active && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Deaktiviert</span>}
                  </div>
                  <button onClick={() => toggleArea(area.id)} data-testid={`fp-toggle-${area.id}`} className="text-[#5C5C59] hover:text-[#1C1C19] p-1">
                    {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
                {!isHidden && (
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {areaTables.length === 0 ? (
                      <div className="col-span-full text-center py-4 text-sm text-[#9CA3AF]">Keine Tische in diesem Bereich</div>
                    ) : areaTables.map(t => {
                      // Combos that include this table
                      const tableComboIds = new Set(
                        (combinations || [])
                          .filter(c => (c.table_ids || []).includes(t.id))
                          .map(c => c.id)
                      );
                      const dayResvs = reservations
                        .filter(r =>
                          !['cancelled', 'declined', 'no_show'].includes(r.status) &&
                          (r.assigned_table_id === t.id || (r.assigned_combination_id && tableComboIds.has(r.assigned_combination_id)))
                        )
                        .sort((a, b) => a.start_time.localeCompare(b.start_time));
                      const state = (fullDayBlockedSet.has(t.id) || fullDayClosedAreaSet.has(t.area_id)) ? 'blocked' : tableBaseStatus(t, dayResvs);
                      // Lila, wenn die reservierte Tischgruppe über mehrere Bereiche geht
                      const reservedComboResv = state === 'reserved'
                        ? dayResvs.find(r => ['confirmed', 'pending'].includes(r.status) && r.assigned_combination_id)
                        : null;
                      const crossAreaCombo = reservedComboResv
                        ? comboSpansMultipleAreas(reservedComboResv.assigned_combination_id, combinations, tables)
                        : false;
                      // Apply filter
                      if (filterMode === 'active' && !t.active) return null;
                      if (filterMode === 'occupied' && !['reserved', 'seated'].includes(state)) return null;
                      if (filterMode === 'free' && state !== 'free') return null;
                      if (filterMode === 'cleaning' && state !== 'cleaning') return null;
                      return (
                        <DroppableTableCard
                          key={t.id}
                          table={t}
                          state={state}
                          crossAreaCombo={crossAreaCombo}
                          dayResvs={dayResvs}
                          dayBlocks={dayBlocksByTable[t.id] || []}
                          onOpenDayBlock={() => setDayBlockTable(t)}
                          onSelectResv={setSelected}
                          onLongPress={() => setTableStatus(t.id, t.status_override === 'cleaning' ? null : 'cleaning')}
                          onVeryLongPress={() => setTableStatus(t.id, t.status_override === 'blocked' ? null : 'blocked')}
                          onAddWalkIn={() => openNewForTable(t, true)}
                          onQuickWalkIn={() => quickWalkIn(t)}
                          onAddReservation={() => openNewForTable(t, false)}
                          onReleaseTable={releaseTable}
                          onSeatGuest={seatGuest}
                          isDragging={!!activeDragResv}
                          isHighlighted={t.id === highlightTableId}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDragResv && (
            <div className="bg-white border-2 border-[#1C1C19] rounded px-2 py-1.5 text-xs flex items-center gap-2 shadow-2xl pointer-events-none">
              <span className="w-1 h-6 rounded" style={{ background: SOURCE_COLOR[activeDragResv.source] }}></span>
              <div>
                <div className="font-mono text-[#1C1C19]">{activeDragResv.start_time}–{activeDragResv.end_time}</div>
                <div className="text-[#5C5C59]">{activeDragResv.customer_name} · {activeDragResv.persons}P</div>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>
      </>)}

      {/* Legende (ganz unten) */}
      <div className="flex flex-wrap gap-3 mt-6 text-xs" data-testid="fp-legend">
        {Object.entries(STATE_LABEL).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ background: STATE_COLOR[k] }}></span>
            <span className="text-[#5C5C59]">{v}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: SPLIT_TILE_COLOR }}></span>
          <span className="text-[#5C5C59]">Gesplittet</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: CROSS_AREA_COLOR }}></span>
          <span className="text-[#5C5C59]">Bereichsübergreifend</span>
        </div>
      </div>

        </div>

        {/* Desktop-only reservation list for the selected date */}
        <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-white border border-[#E5E5E0] rounded-lg max-h-[calc(100vh-8rem)] sticky top-6" data-testid="fp-day-resv-list">
          {/* Waitlist banner — shown when guests are waiting on the selected day */}
          {activeWaitlist.length > 0 && (
            <button
              onClick={() => navigate('/admin/waitlist')}
              data-testid="fp-waitlist-banner"
              className="m-2 mb-0 flex items-center gap-2 px-3 py-2 rounded-md bg-[#FEF9C3] border border-[#FDE68A] text-left hover:bg-[#FEF08A] transition-colors"
            >
              <Clock className="w-4 h-4 text-[#A16207] shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-semibold text-[#854D0E]" data-testid="fp-waitlist-count">
                  {activeWaitlist.length} {activeWaitlist.length === 1 ? 'Gast' : 'Gäste'} auf der Warteliste
                </span>
                <span className="block text-[11px] text-[#A16207]">Zur Warteliste »</span>
              </span>
            </button>
          )}
          <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[#5C5C59] font-medium">Reservierungen</span>
            <span className="text-[11px] text-[#5C5C59]" data-testid="fp-day-resv-count">{dayResvList.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {dayResvList.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] px-3 py-4 text-center" data-testid="fp-day-resv-empty">Keine Reservierungen an diesem Tag.</p>
            ) : dayResvList.map(r => {
              const rTags = tagsForResv(r);
              const offers = Array.isArray(r.selected_offers) ? r.selected_offers : [];
              return (
              <button
                key={r.id}
                onPointerDown={(e) => onListResvPointerDown(e, r)}
                data-testid={`fp-day-resv-${r.id}`}
                title={`${r.customer_name} · ${r.persons} Pers. · ${r.start_time}–${r.end_time} · Tisch ${tableNumbersFor(r)} · Ziehen = auf Tisch legen`}
                style={{ cursor: 'grab', userSelect: 'none', touchAction: 'none' }}
                className="w-full text-left flex items-stretch gap-2 px-3 py-2 rounded-md hover:bg-[#F9F9F7] transition-colors border border-transparent hover:border-[#E5E5E0]"
              >
                <span className="w-1 rounded-full shrink-0 self-stretch" style={{ background: STATUS_COLOR[r.status] || '#9CA3AF' }} />
                <span className="flex-1 min-w-0">
                  {/* Row 1: time (left) · persons + table (right) */}
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-[#1C1C19]">{r.start_time}</span>
                    <span className="text-[10px] text-[#5C5C59] shrink-0 flex items-center gap-1.5">
                      <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{r.persons}</span>
                      <span className="text-[#1C1C19] font-medium">T{tableNumbersFor(r)}</span>
                    </span>
                  </span>
                  {/* Row 2: guest name */}
                  <span className="block text-sm text-[#1C1C19] truncate mt-0.5">{r.customer_name}</span>
                  {/* Row 3: tags (left) · offers (right) */}
                  {(rTags.length > 0 || offers.length > 0) && (
                    <span className="flex items-center justify-between gap-2 mt-1">
                      <span className="flex items-center gap-1 min-w-0 flex-wrap">
                        {rTags.slice(0, 3).map(t => (
                          <span
                            key={t.id}
                            className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full leading-none"
                            style={{ background: (t.color || '#E5E5E0') + '22', color: t.color || '#5C5C59' }}
                          >
                            {t.name}
                          </span>
                        ))}
                        {rTags.length > 3 && <span className="text-[10px] text-[#9CA3AF]">+{rTags.length - 3}</span>}
                      </span>
                      {offers.length > 0 && (
                        <span className="text-[10px] text-[#9A3412] shrink-0 flex items-center gap-0.5" title={offers.map(o => `${o.quantity || 1}× ${o.name}`).join(', ')}>
                          <Wine className="w-3 h-3" />
                          {offers.reduce((a, o) => a + (o.quantity || 1), 0)}
                        </span>
                      )}
                    </span>
                  )}
                  {r.note && (
                    <span className="flex items-center gap-1 text-[10px] text-[#5C5C59] mt-1" title={r.note} data-testid={`fp-day-resv-note-${r.id}`}>
                      <MessageSquare className="w-3 h-3 shrink-0" /><span className="truncate">{r.note}</span>
                    </span>
                  )}
                  {r.internal_note && (
                    <span className="flex items-center gap-1 text-[10px] text-[#B45309] mt-1" title={r.internal_note} data-testid={`fp-day-resv-internal-note-${r.id}`}>
                      <Lock className="w-3 h-3 shrink-0" /><span className="truncate">{r.internal_note}</span>
                    </span>
                  )}
                </span>
              </button>
              );
            })}
          </div>
        </aside>
      </div>

      {listDrag && (
        <div
          className="fixed z-[200] pointer-events-none px-3 py-2 rounded-md bg-[#1C1C19] text-white text-xs shadow-xl flex items-center gap-2"
          style={{ left: listDrag.x + 14, top: listDrag.y + 14 }}
          data-testid="fp-list-drag-ghost"
        >
          <span className="font-mono">{listDrag.resv.start_time}</span>
          <span className="font-medium">{listDrag.resv.customer_name}</span>
          <span className="opacity-70">· {listDrag.resv.persons} P.</span>
        </div>
      )}

      <ReservationDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        reservation={selected}
        tables={tables}
        areas={areas}
        onUpdate={load}
        onAddWalkInForTable={(tableId) => {
          const t = tables.find(x => x.id === tableId);
          setSelected(null);
          openNewForTable(t, true);
        }}
        onAddReservationForTable={(tableId) => {
          const t = tables.find(x => x.id === tableId);
          setSelected(null);
          openNewForTable(t, false);
        }}
      />
      <NewReservationDialog
        open={showNew}
        onClose={() => { setShowNew(false); setDefaultTable(null); }}
        tables={tables}
        areas={areas}
        defaultDate={date}
        defaultTableId={defaultTable?.id}
        onCreated={load}
      />
      <NewReservationDialog
        open={showWalkIn}
        onClose={() => { setShowWalkIn(false); setDefaultTable(null); setSplitMode(false); }}
        tables={tables}
        areas={areas}
        defaultDate={date}
        defaultTableId={defaultTable?.id}
        walkIn
        shareTable={splitMode}
        onCreated={load}
      />

      {swapPrompt && (
        <SwapTablesDialog
          prompt={swapPrompt}
          onCancel={() => setSwapPrompt(null)}
          onConfirm={confirmSwap}
        />
      )}

      {dayBlockTable && (
        <TableDayBlockDialog
          table={dayBlockTable}
          date={date}
          blocks={dayBlocksByTable[dayBlockTable.id] || []}
          existingResvCount={reservations.filter(r =>
            !['cancelled', 'declined', 'no_show', 'completed'].includes(r.status) &&
            r.assigned_table_id === dayBlockTable.id
          ).length}
          onCreate={createDayBlock}
          onDelete={deleteDayBlock}
          onClose={() => setDayBlockTable(null)}
        />
      )}
    </div>
  );
}

function SwapTablesDialog({ prompt, onCancel, onConfirm }) {
  const { sourceResv, targetResv, sourceTable, targetTable } = prompt;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="swap-tables-dialog"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[#E5E5E0]">
          <h3 className="font-display text-xl text-[#1C1C19]">Tisch tauschen?</h3>
          <p className="text-xs text-[#5C5C59] mt-1">
            Der Zieltisch ist zur gleichen Zeit belegt. Beide Reservierungen werden die Tische tauschen.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div className="border border-[#E5E5E0] rounded p-3 text-sm" data-testid="swap-source-info">
            <p className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-1">Verschoben</p>
            <p className="font-medium text-[#1C1C19]">{sourceResv.customer_name} · {sourceResv.persons}P</p>
            <p className="text-xs text-[#5C5C59] font-mono">{sourceResv.start_time}–{sourceResv.end_time}</p>
            <p className="text-xs text-[#5C5C59] mt-1">
              Tisch {sourceTable?.table_number ?? '?'} <span className="mx-1">→</span> Tisch {targetTable?.table_number ?? '?'}
            </p>
          </div>
          <div className="text-center text-[#9CA3AF] text-xs">⇅ tauscht mit ⇅</div>
          <div className="border border-[#E5E5E0] rounded p-3 text-sm" data-testid="swap-target-info">
            <p className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-1">Bisher auf Zieltisch</p>
            <p className="font-medium text-[#1C1C19]">{targetResv.customer_name} · {targetResv.persons}P</p>
            <p className="text-xs text-[#5C5C59] font-mono">{targetResv.start_time}–{targetResv.end_time}</p>
            <p className="text-xs text-[#5C5C59] mt-1">
              Tisch {targetTable?.table_number ?? '?'} <span className="mx-1">→</span> Tisch {sourceTable?.table_number ?? '?'}
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E5E5E0] flex justify-end gap-2">
          <button
            onClick={onCancel}
            data-testid="swap-cancel-btn"
            className="px-4 py-2 text-sm border border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19] hover:text-[#1C1C19] rounded transition"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            data-testid="swap-confirm-btn"
            className="px-4 py-2 text-sm bg-[#1C1C19] hover:bg-[#3A3A35] text-white rounded transition"
          >
            Tische tauschen
          </button>
        </div>
      </div>
    </div>
  );
}

function DraggableReservation({ resv, onSelectResv, onReleaseTable, onSeatGuest }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: resv.id,
  });
  const canRelease = resv.status === 'seated' || resv.status === 'arrived';
  const canSeat = resv.status === 'confirmed' || resv.status === 'pending';
  return (
    <div
      ref={setNodeRef}
      data-testid={`fp-resv-${resv.id}`}
      className={`w-full bg-white border border-[#E5E5E0] rounded text-xs flex items-stretch transition ${isDragging ? 'opacity-30' : 'hover:border-[#1C1C19]'}`}
    >
      <span className="w-1 rounded-l" style={{ background: SOURCE_COLOR[resv.source] }}></span>
      <button
        onClick={(e) => { e.stopPropagation(); onSelectResv(resv); }}
        className="flex-1 text-left px-2 py-1.5 min-w-0"
        data-testid={`fp-resv-open-${resv.id}`}
      >
        <div className="font-mono text-[#1C1C19]">{resv.start_time}–{resv.end_time}</div>
        <div className="text-[#5C5C59] truncate flex items-center gap-1">
          {resv.rebooked_at && <span title="Wetter-/Bereichs-Umbuchung" data-testid={`fp-resv-rebooked-${resv.id}`}>☔</span>}
          {resv.rebooking_needed && <span className="text-[#EF4444]" title="Umbuchung erforderlich">⚠</span>}
          <span>{resv.customer_name} · {resv.persons}P</span>
        </div>
      </button>
      {canSeat && onSeatGuest && (
        <button
          onClick={(e) => { e.stopPropagation(); onSeatGuest(resv); }}
          data-testid={`fp-resv-seat-${resv.id}`}
          aria-label="Gast sitzt"
          title="Gast sitzt"
          className="px-2 flex items-center text-[#F97316] hover:text-white hover:bg-[#F97316] border-l border-[#E5E5E0] transition"
        >
          <UserCheck className="w-3.5 h-3.5" />
        </button>
      )}
      {canRelease && onReleaseTable && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReleaseTable(resv);
          }}
          data-testid={`fp-resv-release-${resv.id}`}
          aria-label="Tisch freigeben"
          title="Tisch freigeben"
          className="px-2 flex items-center text-[#22C55E] hover:text-white hover:bg-[#22C55E] border-l border-[#E5E5E0] transition"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        data-testid={`fp-resv-drag-${resv.id}`}
        className="px-2 flex items-center text-[#9CA3AF] hover:text-[#1C1C19] cursor-grab active:cursor-grabbing touch-none border-l border-[#E5E5E0]"
        aria-label="Reservierung verschieben"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function DroppableTableCard({
  table, state, crossAreaCombo = false, dayResvs, onSelectResv,
  onLongPress, onVeryLongPress, onAddWalkIn, onQuickWalkIn, onAddReservation, onReleaseTable, onSeatGuest, isDragging,
  isHighlighted, dayBlocks = [], onOpenDayBlock,
}) {
  const { isOver, setNodeRef: setDropRef } = useDroppable({ id: table.id });
  // Türkis: teilbarer Tisch, teilweise belegt mit freien Restplätzen (Tages-Spitze)
  const splitOcc = table.split_enabled ? splitOccupancy(table, dayResvs, null) : null;
  const isSplitTile = !!splitOcc && splitOcc.status === 'partial';
  // Bereichsübergreifende Tischgruppe → lila statt blau; gesplitteter Tisch → türkis
  const stateColor = (state === 'reserved' && crossAreaCombo)
    ? CROSS_AREA_COLOR
    : (isSplitTile ? SPLIT_TILE_COLOR : STATE_COLOR[state]);
  const stateLabel = (state === 'reserved' && crossAreaCombo)
    ? 'Reserviert · 2 Bereiche'
    : (isSplitTile ? 'Gesplittet' : STATE_LABEL[state]);

  const hasFullDay = dayBlocks.some(b => b.full_day || !b.start_time || !b.end_time);
  const timedBlocks = dayBlocks.filter(b => !(b.full_day || !b.start_time || !b.end_time));

  const rafRef = useRef(null);
  const startRef = useRef(0);
  const firedCleanRef = useRef(false);
  const firedBlockRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [pressing, setPressing] = useState(false);

  const MAX_MS = 2000;
  const CLEAN_MS = 600;

  const tick = () => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(elapsed / MAX_MS, 1);
    setProgress(p);
    if (elapsed >= CLEAN_MS && !firedCleanRef.current) {
      firedCleanRef.current = true;
      onLongPress();
    }
    if (elapsed >= MAX_MS && !firedBlockRef.current) {
      firedBlockRef.current = true;
      onVeryLongPress();
      stopPress();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const startPress = () => {
    firedCleanRef.current = false;
    firedBlockRef.current = false;
    startRef.current = Date.now();
    setPressing(true);
    setProgress(0);
    rafRef.current = requestAnimationFrame(tick);
  };
  const stopPress = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setPressing(false);
    setProgress(0);
  };
  const handleUp = () => stopPress();

  const stageLabel = progress >= CLEAN_MS / MAX_MS
    ? (progress >= 1 ? 'Sperren' : 'Sperren halten…')
    : 'Reinigung halten…';

  const highlight = isOver && isDragging;

  return (
    <div
      ref={setDropRef}
      data-testid={`fp-table-${table.id}`}
      className={`border-2 rounded-lg p-3 flex flex-col select-none relative overflow-hidden transition-all ${
        highlight ? 'ring-4 ring-[#1C1C19] ring-offset-2 scale-[1.02]' : ''
      } ${isHighlighted ? 'fp-table-pulse' : ''}`}
      style={{ borderColor: stateColor, background: stateColor + '14' }}
    >
      {pressing && (
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#E5E5E0] z-10" data-testid={`fp-progress-${table.id}`}>
          <div className="h-full transition-none" style={{ width: `${progress * 100}%`, background: progress >= CLEAN_MS / MAX_MS ? '#EF4444' : '#9CA3AF' }} />
        </div>
      )}
      {pressing && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#1C1C19] text-white text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider z-20 pointer-events-none">
          {stageLabel}
        </div>
      )}

      <div
        onMouseDown={(e) => { if (e.button !== 0) return; startPress(); }}
        onMouseUp={handleUp}
        onMouseLeave={stopPress}
        onTouchStart={startPress}
        onTouchEnd={handleUp}
        onTouchCancel={stopPress}
        className="flex items-center justify-between mb-2 cursor-pointer touch-none"
        data-testid={`fp-table-head-${table.id}`}
      >
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl text-[#1C1C19]">{table.table_number}</span>
          <span className="text-[10px] uppercase tracking-wider text-[#5C5C59]">{table.min_persons}-{table.max_persons} P.</span>
          {typeof table.priority === 'number' && (
            <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]" data-testid={`fp-priority-${table.id}`}>Prio {table.priority}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {table.online_bookable === false && (
            <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#B45309]/15 text-[#B45309]" data-testid={`fp-inhouse-badge-${table.id}`} title="Nur im Haus – nicht online buchbar">
              Nur Haus
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded" style={{ color: stateColor, background: stateColor + '22' }}>
            {stateLabel}
          </span>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onOpenDayBlock && onOpenDayBlock(); }}
            data-testid={`fp-dayblock-btn-${table.id}`}
            title="Tagessperre verwalten"
            aria-label="Tagessperre verwalten"
            className={`p-1 rounded transition ${dayBlocks.length > 0 ? 'text-[#EF4444]' : 'text-[#9CA3AF] hover:text-[#1C1C19]'}`}
          >
            {dayBlocks.length > 0 ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {dayBlocks.length > 0 && (
        <div className="mb-2 px-2 py-1 rounded text-[10px] bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444]" data-testid={`fp-dayblock-info-${table.id}`}>
          {hasFullDay
            ? <span>Ganztags gesperrt{dayBlocks.find(b => b.note)?.note ? ` · ${dayBlocks.find(b => b.note)?.note}` : ''}</span>
            : <span>Gesperrt: {timedBlocks.map(b => `${b.start_time}–${b.end_time}`).join(', ')}{timedBlocks.find(b => b.note)?.note ? ` · ${timedBlocks.find(b => b.note)?.note}` : ''}</span>}
        </div>
      )}

      <div className="flex-1 space-y-1 min-h-[60px] mb-2">
        {dayResvs.length === 0 ? (
          <div className="text-xs text-[#9CA3AF] italic py-1">Keine Reservierungen heute</div>
        ) : dayResvs.map(r => (
          <DraggableReservation key={r.id} resv={r} onSelectResv={onSelectResv} onReleaseTable={onReleaseTable} onSeatGuest={onSeatGuest} />
        ))}
      </div>

      {dayResvs.length > 1 && (
        <div className="text-[10px] text-[#5C5C59] mb-2 px-2 py-1 bg-[#F9F9F7] rounded border border-[#E5E5E0]" data-testid={`fp-next-${table.id}`}>
          ⏭ Nächste: <span className="font-mono">{dayResvs[1].start_time}</span> · {dayResvs[1].customer_name}
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5 pt-2 border-t" style={{ borderColor: stateColor + '40' }}>
        <button
          onClick={(state === 'free' || state === 'reserved') && onQuickWalkIn ? onQuickWalkIn : onAddWalkIn}
          data-testid={`fp-walkin-${table.id}`}
          disabled={state === 'blocked'}
          title={(state === 'free' || state === 'reserved') ? 'Sofort-Walk-in (2 Pers.)' : 'Walk-in / Tisch teilen'}
          className="text-[11px] py-1.5 px-2 rounded border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1 transition"
        >
          <UserPlus className="w-3 h-3" /> {(state === 'free' || state === 'reserved') ? 'Walk-in ▸' : 'Walk-in'}
        </button>
        <button
          onClick={onAddReservation}
          data-testid={`fp-add-${table.id}`}
          disabled={state === 'blocked'}
          className="text-[11px] py-1.5 px-2 rounded border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1 transition"
        >
          <Plus className="w-3 h-3" /> Zusatz
        </button>
      </div>
    </div>
  );
}


// ===== GRUNDRISS-EDITOR =====

const NODE_W = 116;
const NODE_H = 100;
const CANVAS_H = 560;
const CANVAS_W = 940;

const FP_ZOOM_KEY = 'fp_plan_zoom';

function fitZoomForWidth() {
  if (typeof window === 'undefined') return 1;
  const avail = Math.min(window.innerWidth, 1400) - 48;  // page padding allowance
  return Math.max(0.35, Math.min(1, avail / CANVAS_W));
}

const WAITER_PALETTE = ['#6366F1', '#EF4444', '#22C55E', '#F97316', '#0EA5E9', '#EC4899', '#A855F7', '#14B8A6', '#EAB308', '#64748B'];

function waiterInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
}

function FloorPlanView({
  areas, tables, hiddenAreas, toggleArea, getTableData,
  onSelectResv, onAddReservation, onAddWalkIn, onQuickWalkIn, onSplitWalkIn, onReleaseTable, onSeatGuest, onSaveLayout, highlightTableId,
  waiters, onAssignWaiter, onCreateWaiter, onUpdateWaiter, onDeleteWaiter,
  dayBlocksByTable, onOpenDayBlock, isToday = false,
  openPopoverTableId = null, onPopoverConsumed,
  combinations = [], onReduceCombination, onMoveReservation,
  closedAreaIds = new Set(),
}) {
  const [mode, setMode] = useState('view');  // 'view' | 'edit' | 'assign'
  const [positions, setPositions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [showZones, setShowZones] = useState(false);
  const [selectedWaiterId, setSelectedWaiterId] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [activeAreaId, setActiveAreaId] = useState(null);
  const [zoom, setZoom] = useState(() => {
    try {
      const saved = parseFloat(window.localStorage.getItem(FP_ZOOM_KEY));
      if (!isNaN(saved) && saved >= 0.35 && saved <= 2) return saved;
    } catch { /* noop */ }
    return fitZoomForWidth();
  });
  const [popoverTable, setPopoverTable] = useState(null);  // table whose action popover is open
  const [resvDrag, setResvDrag] = useState(null);  // { resv, x, y } while dragging a reservation onto another table

  // Reservation drag (view mode only): drag a reservation chip onto another table.
  const resvDragEnabled = mode === 'view';
  const handleResvDragStart = (resv, x, y) => setResvDrag({ resv, x, y });
  const handleResvDragMove = (x, y) => setResvDrag(d => (d ? { ...d, x, y } : d));
  const handleResvDragCancel = () => setResvDrag(null);
  const handleResvDragEnd = (resv, x, y) => {
    setResvDrag(null);
    const el = document.elementFromPoint(x, y);
    const node = el && el.closest && el.closest('[data-testid^="fp-plan-table-"]');
    if (!node) return;
    const tid = node.getAttribute('data-testid').replace('fp-plan-table-', '');
    if (tid && tid !== resv.assigned_table_id) onMoveReservation && onMoveReservation(resv.id, tid);
  };

  // Open the action popover for a table requested via URL (?popover=<id>),
  // e.g. tapping a table in the "Heute" mini-plan.
  useEffect(() => {
    if (!openPopoverTableId) return;
    const t = tables.find(x => x.id === openPopoverTableId);
    if (t) {
      setPopoverTable(t);
      onPopoverConsumed && onPopoverConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPopoverTableId, tables]);

  // Persist the chosen zoom so it survives page/tab changes (no more snap-back).
  useEffect(() => {
    try { window.localStorage.setItem(FP_ZOOM_KEY, String(zoom)); } catch { /* noop */ }
  }, [zoom]);

  // Live-Uhr für die Aufenthalts-Ringe (nur heute relevant), Tick alle 30 s.
  const [nowMin, setNowMin] = useState(() => nowMinBerlin());
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => setNowMin(nowMinBerlin()), 30000);
    return () => clearInterval(id);
  }, [isToday]);

  const editMode = mode === 'edit';
  const assignMode = mode === 'assign';

  // Standard-Bereich wählen / gültig halten (Bereiche als Tabs nebeneinander).
  useEffect(() => {
    if (areas.length === 0) return;
    if (!activeAreaId || !areas.some(a => a.id === activeAreaId)) {
      setActiveAreaId(areas[0].id);
    }
  }, [areas, activeAreaId]);
  const activeArea = areas.find(a => a.id === activeAreaId) || areas[0] || null;

  const zoomIn = () => setZoom(z => Math.min(2, +(z * 1.2).toFixed(3)));
  const zoomOut = () => setZoom(z => Math.max(0.35, +(z / 1.2).toFixed(3)));
  const zoomFit = () => setZoom(fitZoomForWidth());

  // Auto-fit only on the very first use (when the user has no saved zoom yet).
  // Once a manual zoom exists, viewport resizes (mobile address bar, rotation)
  // must NOT reset it — the scroll container handles overflow.
  useEffect(() => {
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        let hasSaved = false;
        try { hasSaved = window.localStorage.getItem(FP_ZOOM_KEY) != null; } catch { /* noop */ }
        if (!hasSaved && window.innerWidth < CANVAS_W) setZoom(fitZoomForWidth());
      });
    };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Track desktop breakpoint: on desktop the rooms auto-fit (no scrolling).
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const onR = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  const autoFitRooms = isDesktop && mode !== 'edit';

  const waiterById = {};
  (waiters || []).forEach(w => { waiterById[w.id] = w; });

  // Initialise positions from saved pos_x/pos_y or a sensible default grid.
  useEffect(() => {
    if (dirty || editMode) return;
    const next = {};
    areas.forEach(area => {
      const ats = tables.filter(t => t.area_id === area.id);
      ats.forEach((t, i) => {
        if (typeof t.pos_x === 'number' && typeof t.pos_y === 'number') {
          next[t.id] = { x: t.pos_x, y: t.pos_y };
        } else {
          const col = i % 6;
          const row = Math.floor(i / 6);
          next[t.id] = { x: 16 + col * 120, y: 16 + row * 92 };
        }
      });
    });
    setPositions(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, areas]);

  const handleMove = (id, x, y) => {
    setPositions(prev => ({ ...prev, [id]: { x, y } }));
    setDirty(true);
  };

  const handleSave = async () => {
    const arr = Object.entries(positions).map(([id, p]) => ({ id, pos_x: p.x, pos_y: p.y }));
    await onSaveLayout(arr);
    setDirty(false);
    setMode('view');
  };

  const reinitPositions = () => {
    const next = {};
    areas.forEach(area => {
      tables.filter(t => t.area_id === area.id).forEach((t, i) => {
        if (typeof t.pos_x === 'number' && typeof t.pos_y === 'number') next[t.id] = { x: t.pos_x, y: t.pos_y };
        else { const col = i % 6, row = Math.floor(i / 6); next[t.id] = { x: 16 + col * 120, y: 16 + row * 92 }; }
      });
    });
    setPositions(next);
  };

  const handleCancel = () => {
    setDirty(false);
    setMode('view');
    reinitPositions();
  };

  const handleAssignClick = (table) => {
    if (!selectedWaiterId) {
      toast.info('Bitte zuerst einen Kellner auswählen');
      return;
    }
    // toggle: clicking a table already in this waiter's zone clears it
    const next = table.waiter_id === selectedWaiterId ? null : selectedWaiterId;
    onAssignWaiter(table.id, next);
  };

  const MODE_BTNS = [
    { v: 'view', l: 'Ansehen', icon: <Eye className="w-3.5 h-3.5" /> },
    { v: 'edit', l: 'Bearbeiten', icon: <Pencil className="w-3.5 h-3.5" /> },
    { v: 'assign', l: 'Kellner zuweisen', icon: <UserCheck className="w-3.5 h-3.5" /> },
  ];

  return (
    <div data-testid="fp-plan-view">
      {/* Bereich-Tabs (nebeneinander) — Tischplan direkt darunter */}
      {areas.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="fp-plan-area-tabs">
          {areas.map(area => {
            const n = tables.filter(t => t.area_id === area.id).length;
            const active = activeArea && area.id === activeArea.id;
            return (
              <button
                key={area.id}
                onClick={() => setActiveAreaId(area.id)}
                data-testid={`fp-plan-area-tab-${area.id}`}
                className={`px-3 py-1.5 rounded-md text-sm border flex items-center gap-2 transition ${
                  active ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: area.color }}></span>
                {area.name}
                <span className={`text-xs ${active ? 'text-white/70' : 'text-[#9CA3AF]'}`}>({n})</span>
                {!area.active && <span className="text-[10px] px-1.5 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Aus</span>}
                {closedAreaIds.has(area.id) && <Lock className={`w-3 h-3 ${active ? 'text-white' : 'text-[#EF4444]'}`} />}
              </button>
            );
          })}
        </div>
      )}

      {/* Tischplan: aktiver Bereich (ganz oben) */}
      <div className="space-y-6">
        {areas.length === 0 && (
          <div className="text-center py-12 bg-white border border-dashed border-[#E5E5E0] rounded-lg" data-testid="fp-plan-empty-areas">
            <p className="text-[#5C5C59]">Noch keine Bereiche angelegt. Bitte zuerst Bereiche und Tische erstellen.</p>
          </div>
        )}
        {activeArea && (() => {
          const area = activeArea;
          const areaTables = tables.filter(t => t.area_id === area.id);
          return (
            <div key={area.id} className="bg-white border border-[#E5E5E0] rounded-lg" data-testid={`fp-plan-area-${area.id}`}>
              <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: area.color }}></span>
                  <h2 className="font-display text-lg text-[#1C1C19]">{area.name}</h2>
                  <span className="text-xs text-[#5C5C59]">({areaTables.length} Tische)</span>
                  {!area.active && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Deaktiviert</span>}
                  {closedAreaIds.has(area.id) && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded flex items-center gap-1"><Lock className="w-3 h-3" /> Heute gesperrt</span>}
                </div>
              </div>
              <AreaCanvas
                areaTables={areaTables}
                positions={positions}
                editMode={editMode}
                assignMode={assignMode}
                showZones={showZones}
                waiterById={waiterById}
                onMove={handleMove}
                getTableData={getTableData}
                onTableClick={(t) => setPopoverTable(t)}
                onAssignClick={handleAssignClick}
                highlightTableId={highlightTableId}
                combinations={combinations}
                allTables={tables}
                autoFit={autoFitRooms}
                dayBlocksByTable={dayBlocksByTable}
                onOpenDayBlock={onOpenDayBlock}
                zoom={zoom}
                isToday={isToday}
                nowMin={nowMin}
                resvDragEnabled={resvDragEnabled}
                dragResv={resvDrag ? resvDrag.resv : null}
                onResvDragStart={handleResvDragStart}
                onResvDragMove={handleResvDragMove}
                onResvDragEnd={handleResvDragEnd}
                onResvDragCancel={handleResvDragCancel}
              />
            </div>
          );
        })()}
      </div>

      {/* === Steuerung / Einstellungen ganz unten === */}
      <div className="mt-6 pt-5 border-t border-[#E5E5E0] space-y-3" data-testid="fp-plan-controls">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-[#5C5C59] max-w-md">
            {editMode
              ? 'Bearbeiten aktiv — Tische frei verschieben, dann speichern.'
              : assignMode
                ? 'Kellner auswählen, dann Tische antippen, um sie der Zone zuzuordnen (erneut tippen = entfernen).'
                : 'Tippen auf einen Tisch zeigt die Reservierungen. Eine Reservierung an ihrer Uhrzeit auf einen anderen Tisch ziehen = Tisch wechseln (freier Tisch: verschieben · belegt: Tausch-Dialog · zu klein: nicht möglich).'}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {editMode ? (
              <>
                <button onClick={handleCancel} data-testid="fp-plan-cancel" className="px-3 py-1.5 rounded-md text-xs border border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19] hover:text-[#1C1C19] flex items-center gap-1.5 transition">
                  <X className="w-3.5 h-3.5" /> Abbrechen
                </button>
                <button onClick={handleSave} disabled={!dirty} data-testid="fp-plan-save" className="px-3 py-1.5 rounded-md text-xs bg-[#1C1C19] hover:bg-[#3A3A35] text-white flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition">
                  <Save className="w-3.5 h-3.5" /> Speichern
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1" data-testid="fp-plan-mode-toggle">
                  {MODE_BTNS.map(b => (
                    <button
                      key={b.v}
                      onClick={() => setMode(b.v)}
                      data-testid={`fp-plan-mode-${b.v}`}
                      className={`px-2.5 py-1.5 rounded-md text-xs border flex items-center gap-1.5 transition ${
                        mode === b.v ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
                      }`}
                    >
                      {b.icon} {b.l}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowZones(z => !z)}
                  data-testid="fp-plan-zones-toggle"
                  className={`px-2.5 py-1.5 rounded-md text-xs border flex items-center gap-1.5 transition ${
                    showZones ? 'bg-[#6366F1] text-white border-[#6366F1]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
                  }`}
                >
                  Zonen {showZones ? 'an' : 'aus'}
                </button>
                <button onClick={() => setShowManager(true)} data-testid="fp-plan-manage-waiters" className="px-2.5 py-1.5 rounded-md text-xs border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white flex items-center gap-1.5 transition">
                  <UserPlus className="w-3.5 h-3.5" /> Kellner verwalten
                </button>
              </>
            )}
          </div>
        </div>

        {/* Zoom-Steuerung (mobil/Tablet) */}
        {!autoFitRooms && (
          <div className="flex items-center gap-1.5" data-testid="fp-plan-zoom">
            <button onClick={zoomOut} data-testid="fp-plan-zoom-out" aria-label="Verkleinern" className="w-8 h-8 rounded-md border border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19] hover:text-[#1C1C19] flex items-center justify-center transition text-lg leading-none">−</button>
            <span className="text-xs text-[#5C5C59] w-12 text-center tabular-nums" data-testid="fp-plan-zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} data-testid="fp-plan-zoom-in" aria-label="Vergrößern" className="w-8 h-8 rounded-md border border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19] hover:text-[#1C1C19] flex items-center justify-center transition text-lg leading-none">+</button>
            <button onClick={zoomFit} data-testid="fp-plan-zoom-fit" className="px-2.5 h-8 rounded-md border border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19] hover:text-[#1C1C19] flex items-center gap-1.5 transition text-xs">
              <Maximize2 className="w-3.5 h-3.5" /> Anpassen
            </button>
          </div>
        )}

        {/* Kellner-Auswahlleiste im Zuweisen-Modus */}
        {assignMode && (
          <div className="flex items-center gap-2 flex-wrap p-3 bg-[#F9F9F7] border border-[#E5E5E0] rounded-lg" data-testid="fp-waiter-picker">
            {(waiters || []).length === 0 ? (
              <span className="text-xs text-[#5C5C59]">Noch keine Kellner angelegt — über „Kellner verwalten" hinzufügen.</span>
            ) : (
              (waiters || []).map(w => (
                <button
                  key={w.id}
                  onClick={() => setSelectedWaiterId(prev => prev === w.id ? null : w.id)}
                  data-testid={`fp-waiter-chip-${w.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs border flex items-center gap-1.5 transition ${
                    selectedWaiterId === w.id ? 'text-white' : 'text-[#1C1C19] bg-white'
                  }`}
                  style={{
                    borderColor: w.color,
                    background: selectedWaiterId === w.id ? w.color : 'white',
                  }}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: selectedWaiterId === w.id ? 'white' : w.color }}></span>
                  {w.name}
                </button>
              ))
            )}
          </div>
        )}

        {/* Zonen-Legende */}
        {showZones && (waiters || []).length > 0 && (
          <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="fp-zones-legend">
            <span className="text-[#5C5C59] uppercase tracking-wider text-[10px]">Zonen:</span>
            {(waiters || []).map(w => (
              <span key={w.id} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ background: w.color }}></span>
                <span className="text-[#5C5C59]">{w.name}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {showManager && (
        <WaiterManagerDialog
          waiters={waiters || []}
          onClose={() => setShowManager(false)}
          onCreate={onCreateWaiter}
          onUpdate={onUpdateWaiter}
          onDelete={onDeleteWaiter}
        />
      )}

      {popoverTable && (() => {
        const { dayResvs, state } = getTableData(popoverTable);
        return (
          <TableActionPopover
            table={popoverTable}
            state={state}
            dayResvs={dayResvs}
            dayBlocks={(dayBlocksByTable && dayBlocksByTable[popoverTable.id]) || []}
            tables={tables}
            combinations={combinations}
            onReduceCombination={onReduceCombination}
            onClose={() => setPopoverTable(null)}
            onSelectResv={(r) => { onSelectResv(r); setPopoverTable(null); }}
            onReleaseTable={(r) => { onReleaseTable(r); setPopoverTable(null); }}
            onSeatGuest={(r) => { onSeatGuest(r); setPopoverTable(null); }}
            onAddWalkIn={() => { onAddWalkIn(popoverTable); setPopoverTable(null); }}
            onQuickWalkIn={() => { onQuickWalkIn(popoverTable); setPopoverTable(null); }}
            onSplitWalkIn={() => { onSplitWalkIn(popoverTable); setPopoverTable(null); }}
            onAddReservation={() => { onAddReservation(popoverTable); setPopoverTable(null); }}
          />
        );
      })()}

      {/* Drag-Ghost: folgt dem Finger/der Maus beim Verschieben einer Reservierung */}
      {resvDrag && (
        <div
          data-testid="fp-resv-drag-ghost"
          className="fixed z-[60] pointer-events-none -translate-x-1/2 -translate-y-1/2 bg-white border-2 border-[#1C1C19] rounded-md px-2 py-1 text-xs flex items-center gap-1.5 shadow-2xl"
          style={{ left: resvDrag.x, top: resvDrag.y }}
        >
          <span className="font-mono font-semibold text-[#1C1C19]">{resvDrag.resv.start_time}</span>
          <span className="text-[#5C5C59]">{resvDrag.resv.customer_name} · {resvDrag.resv.persons}P</span>
        </div>
      )}
    </div>
  );
}

function AreaCanvas({
  areaTables, positions, editMode, assignMode, showZones, waiterById,
  onMove, getTableData, onTableClick, onAssignClick, highlightTableId,
  combinations = [], allTables = [], autoFit = false,
  dayBlocksByTable, onOpenDayBlock, zoom = 1, isToday = false, nowMin = null,
  resvDragEnabled = false, dragResv = null, onResvDragStart, onResvDragMove, onResvDragEnd, onResvDragCancel,
}) {
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const [containerW, setContainerW] = useState(0);

  // Measure the available width so we can fit the whole room on desktop.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setContainerW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // In view mode the canvas grows to fit its content; in edit mode we keep the
  // full fixed grid so there is room to rearrange tables.
  const bounds = (() => {
    if (editMode) return { w: CANVAS_W, h: CANVAS_H };
    let maxX = 0, maxY = 0;
    areaTables.forEach(t => {
      const p = positions[t.id] || { x: t.pos_x || 16, y: t.pos_y || 16 };
      maxX = Math.max(maxX, (p.x || 0) + NODE_W);
      maxY = Math.max(maxY, (p.y || 0) + NODE_H);
    });
    return { w: Math.max(360, maxX + 40), h: Math.max(220, maxY + 40) };
  })();

  // Desktop: auto-fit the room to the container width (no horizontal scroll, ≤100%).
  const effectiveScale = (autoFit && containerW > 0) ? Math.min(1, (containerW - 4) / bounds.w) : zoom;

  return (
    <div
      ref={scrollRef}
      data-testid="fp-canvas-scroll"
      className={`m-4 rounded-md border border-[#E5E5E0] ${autoFit ? 'overflow-hidden' : 'overflow-auto'}`}
      style={autoFit ? {} : { maxHeight: 'min(70vh, 560px)', WebkitOverflowScrolling: 'touch' }}
    >
      {/* Sizer drives the scrollbars for the scaled canvas */}
      <div style={{ width: bounds.w * effectiveScale, height: bounds.h * effectiveScale }}>
        <div
          ref={canvasRef}
          data-testid="fp-canvas"
          className="relative"
          style={{
            width: bounds.w,
            height: bounds.h,
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top left',
            background: editMode
              ? 'radial-gradient(#E5E5E0 1px, transparent 1px) 0 0 / 24px 24px, #FBFBF9'
              : '#FBFBF9',
          }}
        >
          {areaTables.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-[#9CA3AF]">Keine Tische in diesem Bereich</div>
          ) : areaTables.map(t => {
            const pos = positions[t.id] || { x: 16, y: 16 };
            const { dayResvs, state } = getTableData(t);
            const waiter = t.waiter_id ? waiterById[t.waiter_id] : null;
            const blocks = (dayBlocksByTable && dayBlocksByTable[t.id]) || [];
            return (
              <PlanTableNode
                key={t.id}
                table={t}
                pos={pos}
                state={state}
                dayResvs={dayResvs}
                editMode={editMode}
                assignMode={assignMode}
                showZones={showZones}
                waiter={waiter}
                dayBlocks={blocks}
                zoom={zoom}
                isToday={isToday}
                nowMin={nowMin}
                onOpenDayBlock={() => onOpenDayBlock && onOpenDayBlock(t)}
                onMove={onMove}
                canvasRef={canvasRef}
                isHighlighted={t.id === highlightTableId}
                combinations={combinations}
                allTables={allTables}
                onClick={() => onTableClick(t)}
                onAssignClick={() => onAssignClick(t)}
                resvDragEnabled={resvDragEnabled}
                dragResv={dragResv}
                onResvDragStart={onResvDragStart}
                onResvDragMove={onResvDragMove}
                onResvDragEnd={onResvDragEnd}
                onResvDragCancel={onResvDragCancel}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PlanTableNode({
  table, pos, state, dayResvs, editMode, assignMode, showZones, waiter,
  onMove, canvasRef, isHighlighted, onClick, onAssignClick, dayBlocks = [], onOpenDayBlock, zoom = 1,
  isToday = false, nowMin = null, combinations = [], allTables = [],
  resvDragEnabled = false, dragResv = null, onResvDragStart, onResvDragMove, onResvDragEnd, onResvDragCancel,
}) {
  const dragRef = useRef(null);
  const resvDragRef = useRef(null);
  const resvDraggedRef = useRef(false);
  const hasBlock = dayBlocks.length > 0;

  // Reservation chip drag (view mode): pick up a reservation and drop it on another table.
  const beginResvDrag = (e, resv) => {
    if (!resvDragEnabled || !resv) return;
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    resvDragRef.current = { resv, startX: e.clientX, startY: e.clientY, moved: false, el: e.currentTarget, pid: e.pointerId };
    resvDraggedRef.current = false;
  };
  const moveResvDrag = (e) => {
    const d = resvDragRef.current;
    if (!d) return;
    e.stopPropagation();
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 6) {
      d.moved = true;
      onResvDragStart && onResvDragStart(d.resv, e.clientX, e.clientY);
    }
    if (d.moved) onResvDragMove && onResvDragMove(e.clientX, e.clientY);
  };
  const endResvDrag = (e) => {
    const d = resvDragRef.current;
    resvDragRef.current = null;
    if (!d) return;
    e.stopPropagation();
    try { d.el.releasePointerCapture(d.pid); } catch { /* noop */ }
    if (d.moved) {
      resvDraggedRef.current = true;  // suppress the follow-up click (which would open the popover)
      onResvDragEnd && onResvDragEnd(d.resv, e.clientX, e.clientY);
    } else {
      onResvDragCancel && onResvDragCancel();
    }
  };
  // Props applied to a draggable reservation chip/row.
  const chipDrag = (resv) => (resvDragEnabled ? {
    onPointerDown: (e) => beginResvDrag(e, resv),
    onPointerMove: moveResvDrag,
    onPointerUp: endResvDrag,
    onClick: (e) => { if (resvDraggedRef.current) { e.stopPropagation(); resvDraggedRef.current = false; } },
    style: { touchAction: 'none', cursor: 'grab' },
    title: `${resv.start_time}–${resv.end_time} · ${resv.customer_name} · ziehen, um den Tisch zu wechseln`,
  } : {});

  const onPointerDown = (e) => {
    if (!editMode) return;
    if (e.button !== undefined && e.button !== 0) return;  // primary button only
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    // Convert screen-pixel delta to logical canvas pixels (account for zoom scale)
    const dx = (e.clientX - d.startX) / zoom;
    const dy = (e.clientY - d.startY) / zoom;
    const nx = Math.max(0, Math.min(CANVAS_W - NODE_W, d.origX + dx));
    const ny = Math.max(0, Math.min(CANVAS_H - NODE_H, d.origY + dy));
    onMove(table.id, nx, ny);
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const occupant = dayResvs[0];
  // Lila-Markierung: reservierte Tischgruppe, die über mehrere Bereiche geht
  const reservedComboResv = state === 'reserved'
    ? dayResvs.find(r => ['confirmed', 'pending'].includes(r.status) && r.assigned_combination_id)
    : null;
  const crossAreaCombo = reservedComboResv
    ? comboSpansMultipleAreas(reservedComboResv.assigned_combination_id, combinations, allTables)
    : false;
  // Smart-Tischsplitting: Restkapazität (jetzt bei "heute", sonst Tages-Spitze)
  const split = table.split_enabled
    ? splitOccupancy(table, dayResvs, (isToday && nowMin != null) ? nowMin : null)
    : null;
  // Türkis: teilbarer Tisch, der teilweise belegt ist und noch freie Restplätze hat
  const isSplitTile = !!split && split.status === 'partial';
  const statusColor = (state === 'reserved' && crossAreaCombo)
    ? CROSS_AREA_COLOR
    : (isSplitTile ? SPLIT_TILE_COLOR : STATE_COLOR[state]);
  const zoneActive = showZones && !!waiter;

  // Gruppen-/Kombinations-Info des belegenden Gastes (für Service-Kennzeichnung).
  const occCombo = occupant?.assigned_combination_id
    ? (combinations || []).find(c => c.id === occupant.assigned_combination_id)
    : null;
  const isGroup = !!occCombo && (occCombo.table_ids || []).length > 1;
  const groupNums = isGroup
    ? (occCombo.table_ids || []).map(tid => (allTables || []).find(t => t.id === tid)?.table_number).filter(Boolean)
    : [];

  // Aufenthalts-Ring: nur heute + für den aktuell sitzenden Gast.
  const seatedResv = dayResvs.find(r => ['seated', 'arrived'].includes(r.status));
  const ringResv = (isToday && nowMin != null && seatedResv) ? seatedResv : null;
  const ringStart = ringResv ? hmToMin(ringResv.start_time) : null;
  const ringEnd = ringResv ? hmToMin(ringResv.end_time) : null;
  const showRing = !!ringResv && ringStart != null && ringEnd != null && ringEnd > ringStart;

  // Live-Hervorhebung während eine Reservierung gezogen wird:
  // grün = Tausch/Verschieben möglich, rot = zu klein oder gesperrt.
  let dragHl = null;
  if (dragResv && dragResv.assigned_table_id !== table.id) {
    const isFullyBlocked = (dayBlocks || []).some(b => b.full_day || !b.start_time || !b.end_time);
    const tooSmall = !dragResv.assigned_combination_id && dragResv.persons > (table.max_persons || 0);
    dragHl = (isFullyBlocked || tooSmall) ? 'invalid' : 'valid';
  }

  const borderColor = zoneActive ? waiter.color : statusColor;
  const background = showZones
    ? (waiter ? waiter.color + '2E' : '#9CA3AF14')
    : statusColor + (crossAreaCombo ? '40' : (isSplitTile ? '33' : '1A'));

  const handleClick = editMode ? undefined : (assignMode ? onAssignClick : onClick);

  return (
    <div
      data-testid={`fp-plan-table-${table.id}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={handleClick}
      className={`absolute rounded-lg border-2 shadow-sm flex flex-col items-center justify-center text-center px-1.5 select-none transition-shadow ${
        editMode ? 'cursor-move hover:shadow-md' : 'cursor-pointer hover:shadow-md'
      } ${assignMode ? 'hover:ring-2 hover:ring-[#6366F1] hover:ring-offset-1' : ''} ${isHighlighted ? 'fp-table-pulse' : ''}`}
      style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H, borderColor, background, touchAction: editMode ? 'none' : 'auto' }}
    >
      {/* Drag-Ziel-Hervorhebung: grün = möglich, rot = zu klein/gesperrt */}
      {dragHl && (
        <span
          data-testid={`fp-plan-drophl-${table.id}`}
          className="absolute inset-0 rounded-lg pointer-events-none z-20"
          style={{
            boxShadow: `0 0 0 3px ${dragHl === 'valid' ? '#22C55E' : '#EF4444'}`,
            background: dragHl === 'valid' ? '#22C55E26' : '#EF444426',
          }}
          aria-hidden="true"
        ></span>
      )}      {/* Aufenthalts-Timer oben links */}
      {showRing && (
        <div className="absolute top-0.5 left-0.5 z-10" data-testid={`fp-plan-stayring-${table.id}`}>
          <StayRing startMin={ringStart} endMin={ringEnd} nowMin={nowMin} size={26} stroke={3} />
        </div>
      )}
      {/* Status-Punkt oben links (sichtbar auch im Zonen-Modus, außer wenn Timer dort liegt) */}
      {showZones && !showRing && (
        <span className="absolute top-1 left-1 w-2.5 h-2.5 rounded-full border border-white" style={{ background: statusColor }} title={STATE_LABEL[state]}></span>
      )}
      {/* Kellner-Badge oben rechts */}
      {waiter && (
        <span
          data-testid={`fp-plan-waiter-badge-${table.id}`}
          className="absolute top-1 right-1 text-[8px] font-bold text-white rounded px-1 py-0.5 leading-none"
          style={{ background: waiter.color }}
          title={waiter.name}
        >
          {waiterInitials(waiter.name)}
        </span>
      )}
      {/* Gruppen-Kennzeichnung: Symbol + verbundene Tischnummern + gestrichelte Umrandung */}
      {isGroup && (
        <>
          <span className="absolute inset-0 rounded-lg border-2 border-dashed pointer-events-none" style={{ borderColor: '#6366F1' }} aria-hidden="true"></span>
          <span
            data-testid={`fp-plan-group-badge-${table.id}`}
            className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-0.5 bg-[#6366F1] text-white text-[8px] font-bold rounded-full px-1.5 py-0.5 shadow leading-none whitespace-nowrap"
            title={`Gruppe – Tisch ${groupNums.join(' + ')}`}
          >
            <Link2 className="w-2.5 h-2.5" /> {groupNums.join('+')}
          </span>
        </>
      )}
      {/* Smart-Tischsplitting: Restplatz-Badge (belegt/Kapazität) */}
      {split && split.occupied > 0 && (
        <span
          data-testid={`fp-plan-split-${table.id}`}
          className="absolute bottom-1 right-1 z-10 inline-flex items-center gap-0.5 text-[8px] font-bold rounded px-1 py-0.5 leading-none shadow-sm text-white"
          style={{ background: split.status === 'full' ? '#EF4444' : '#F59E0B' }}
          title={`Splitting: ${split.occupied}/${split.cap} belegt · ${split.free} frei · ${split.count} Teilreservierung(en)`}
        >
          {split.occupied}/{split.cap}
        </span>
      )}
      {/* Tagessperre-Button + Indikator unten links */}
      {!editMode && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onOpenDayBlock && onOpenDayBlock(); }}
          data-testid={`fp-plan-dayblock-btn-${table.id}`}
          title="Tagessperre verwalten"
          aria-label="Tagessperre verwalten"
          className="absolute bottom-1 left-1 p-0.5 rounded"
        >
          {hasBlock ? <Lock className="w-3 h-3 text-[#EF4444]" /> : <Unlock className="w-3 h-3 text-[#9CA3AF] hover:text-[#1C1C19]" />}
        </button>
      )}
      <span className="font-display text-xl leading-none text-[#1C1C19]">{table.table_number}</span>
      {table.online_bookable === false && (
        <span className="absolute top-0.5 right-0.5 text-[8px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-[#B45309] text-white" data-testid={`fp-plan-inhouse-badge-${table.id}`} title="Nur im Haus – nicht online buchbar">Haus</span>
      )}
      {dayResvs.length > 1 ? (
        /* Mehrfach reserviert/belegt: jede Reservierung mit Uhrzeit + Name untereinander */
        <div className="flex flex-col items-stretch gap-0.5 mt-1 w-full px-0.5" data-testid={`fp-plan-multi-${table.id}`}>
          {dayResvs.slice(0, 3).map(r => {
            const seated = ['seated', 'arrived'].includes(r.status);
            return (
              <div
                key={r.id}
                data-testid={`fp-plan-resv-${table.id}-${r.id}`}
                className="flex items-center gap-1 min-w-0 leading-none rounded"
                {...chipDrag(r)}
              >
                <span
                  data-testid={`fp-plan-time-${table.id}-${r.id}`}
                  className="text-[9px] font-mono font-semibold px-1 rounded text-white shrink-0 leading-tight"
                  style={{ background: seated ? STATE_COLOR.seated : STATE_COLOR.reserved }}
                >
                  {r.start_time}
                </span>
                <span className="text-[9px] font-semibold text-[#1C1C19] truncate">{r.customer_name}</span>
                {r.offer && ['sent', 'accepted'].includes(r.offer.status) && (
                  <span title="Angebot vorhanden" data-testid={`fp-offer-dot-${r.id}`} className="inline-block w-1.5 h-1.5 rounded-full bg-[#16A34A] shrink-0" />
                )}
              </div>
            );
          })}
          {dayResvs.length > 3 && (
            <span className="text-[8px] text-[#5C5C59] leading-none">+{dayResvs.length - 3} weitere</span>
          )}
        </div>
      ) : occupant ? (
        /* Einzelne Reservierung/Belegung: Name + Uhrzeit-Chip */
        <>
          <span
            data-testid={`fp-plan-guest-${table.id}`}
            className="text-[9px] font-semibold text-[#1C1C19] leading-none mt-0.5 truncate max-w-full px-0.5"
            title={`${occupant.customer_name} · ${occupant.persons} Pers.`}
          >
            {occupant.customer_name}
            {occupant.offer && ['sent', 'accepted'].includes(occupant.offer.status) && (
              <span title="Angebot vorhanden" data-testid={`fp-offer-dot-${occupant.id}`} className="inline-block w-1.5 h-1.5 rounded-full bg-[#16A34A] ml-1 align-middle" />
            )}
          </span>
          <div
            className="flex items-center justify-center mt-0.5 max-w-full rounded"
            data-testid={`fp-plan-resv-${table.id}-${occupant.id}`}
            {...chipDrag(occupant)}
          >
            <span
              data-testid={`fp-plan-time-${table.id}-${occupant.id}`}
              className="text-[11px] font-mono font-semibold px-1 rounded text-white leading-tight"
              style={{ background: ['seated', 'arrived'].includes(occupant.status) ? (isSplitTile ? SPLIT_TILE_COLOR : STATE_COLOR.seated) : (crossAreaCombo ? CROSS_AREA_COLOR : (isSplitTile ? SPLIT_TILE_COLOR : STATE_COLOR.reserved)) }}
            >
              {occupant.start_time}
            </span>
          </div>
        </>
      ) : (
        /* Freier Tisch: Personenkapazität + Status */
        <>
          <span className="text-[9px] uppercase tracking-wider text-[#5C5C59] mt-0.5">{table.min_persons}-{table.max_persons} P.</span>
          <span className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: statusColor }}>{STATE_LABEL[state]}</span>
        </>
      )}
    </div>
  );
}

export function TableActionPopover({ table, state, dayResvs, dayBlocks = [], tables = [], combinations = [], onReduceCombination, onClose, onSelectResv, onReleaseTable, onSeatGuest, onAddWalkIn, onQuickWalkIn, onSplitWalkIn, onAddReservation }) {
  const color = STATE_COLOR[state] || '#5C5C59';
  const blocked = state === 'blocked';
  const hasFullDay = dayBlocks.some(b => b.full_day || !b.start_time || !b.end_time);
  const sorted = [...dayResvs].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const [splitFor, setSplitFor] = useState(null);  // reservation id whose combo-split chooser is open
  const [keepSel, setKeepSel] = useState([]);      // table ids to KEEP when splitting a combination

  // Walk-in "Tisch teilen": only when the table is currently held by walk-in(s) and seats remain.
  const walkinOccupants = sorted.filter(r => r.source === 'walk_in' && ['seated', 'arrived', 'confirmed'].includes(r.status));
  const usedSeats = walkinOccupants.reduce((s, r) => s + (parseInt(r.persons) || 0), 0);
  const canSplitWalkin = !blocked && walkinOccupants.length > 0 && usedSeats < (table.max_persons || 0);

  const comboTablesFor = (r) => {
    if (!r.assigned_combination_id) return [];
    const c = combinations.find(c => c.id === r.assigned_combination_id);
    return (c?.table_ids || []).map(tid => tables.find(t => t.id === tid)).filter(Boolean);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="fp-table-popover"
      onClick={onClose}
    >
      <div className="bg-white rounded-xl max-w-sm w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between" style={{ background: color + '12' }}>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-display text-2xl text-[#1C1C19]">Tisch {table.table_number}</span>
            <span className="text-[11px] uppercase tracking-wider text-[#5C5C59]">{table.min_persons}-{table.max_persons} P.</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded" style={{ color, background: color + '22' }}>
              {STATE_LABEL[state]}
            </span>
            <button onClick={onClose} data-testid="fp-popover-close" className="text-[#5C5C59] hover:text-[#1C1C19] p-1"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {dayBlocks.length > 0 && (
          <div className="mx-3 mt-3 px-2 py-1 rounded text-[11px] bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444]" data-testid="fp-popover-blockinfo">
            {hasFullDay
              ? <span>Ganztags gesperrt{dayBlocks.find(b => b.note)?.note ? ` · ${dayBlocks.find(b => b.note)?.note}` : ''}</span>
              : <span>Gesperrt: {dayBlocks.filter(b => b.start_time && b.end_time).map(b => `${b.start_time}–${b.end_time}`).join(', ')}</span>}
          </div>
        )}

        {table.split_enabled && (() => {
          const s = splitOccupancy(table, sorted, null);
          const label = s.status === 'free' ? 'Frei' : (s.status === 'full' ? 'Voll belegt' : 'Teilweise belegt');
          const c = s.status === 'free' ? '#22C55E' : (s.status === 'full' ? '#EF4444' : '#F59E0B');
          return (
            <div className="mx-3 mt-3 px-3 py-2 rounded-lg border" style={{ borderColor: c + '55', background: c + '12' }} data-testid="fp-popover-split">
              <div className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 font-medium" style={{ color: c }}>
                  <Users className="w-3.5 h-3.5" /> Smart-Splitting · {label}
                </span>
                <span className="text-[#5C5C59]">Spitze: {s.occupied}/{s.cap} · frei {s.free}</span>
              </div>
              {s.count > 0 && (
                <div className="mt-1 text-[11px] text-[#5C5C59]">{s.count} gleichzeitige Teilreservierung(en)</div>
              )}
            </div>
          );
        })()}

        <div className="p-3 max-h-[50vh] overflow-y-auto space-y-1.5" data-testid="fp-popover-list">
          {sorted.length === 0 ? (
            <div className="text-sm text-[#9CA3AF] italic py-4 text-center" data-testid="fp-popover-empty">Keine Reservierungen heute</div>
          ) : sorted.map(r => {
            const comboTables = comboTablesFor(r);
            const isCombo = comboTables.length >= 2;
            return (
              <div key={r.id} className="rounded-lg border border-[#E5E5E0] overflow-hidden">
                <button
                  onClick={() => onSelectResv(r)}
                  data-testid={`fp-popover-resv-${r.id}`}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[#F9F9F7] transition"
                >
                  <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: STATUS_COLOR[r.status] || color }}></span>
                  <span className="font-mono text-sm text-[#1C1C19] shrink-0">{r.start_time}<span className="text-[#9CA3AF]">–{r.end_time}</span></span>
                  <span className="flex-1 min-w-0 text-sm text-[#1C1C19] truncate">{r.customer_name}{isCombo && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#5C5C59]">Gruppe {comboTables.map(t => t.table_number).join('+')}</span>}</span>
                  <span className="text-xs text-[#5C5C59] shrink-0 inline-flex items-center gap-0.5"><Users className="w-3 h-3" />{r.persons}</span>
                </button>
                {['confirmed', 'pending'].includes(r.status) && onSeatGuest && (
                  <div className="px-3 pb-2 pt-1.5 bg-[#EFF6FF] border-t border-[#BFDBFE]">
                    <button
                      onClick={() => { onSeatGuest(r); }}
                      data-testid={`fp-popover-seat-${r.id}`}
                      title={`Gast sitzt (${r.customer_name})`}
                      className="w-full text-xs py-1.5 px-2 rounded border border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white transition inline-flex items-center justify-center gap-1.5 font-medium"
                    >
                      <UserCheck className="w-3.5 h-3.5" /> Gast sitzt · {r.customer_name}
                    </button>
                  </div>
                )}
                {['seated', 'arrived'].includes(r.status) && onReleaseTable && (
                  <div className="px-3 pb-2 pt-1.5 bg-[#FFF7ED] border-t border-[#FED7AA]">
                    <button
                      onClick={() => { onReleaseTable(r); }}
                      data-testid={`fp-popover-release-${r.id}`}
                      title={`Tisch freigeben (${r.customer_name})`}
                      className="w-full text-xs py-1.5 px-2 rounded border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white transition inline-flex items-center justify-center gap-1.5 font-medium"
                    >
                      <Check className="w-3.5 h-3.5" /> Tisch freigeben · {r.customer_name}
                    </button>
                  </div>
                )}
                {isCombo && onReduceCombination && (
                  <div className="px-3 pb-2 pt-1 bg-[#F9F9F7] border-t border-[#E5E5E0]">
                    {splitFor === r.id ? (
                      <div data-testid={`fp-popover-split-chooser-${r.id}`}>
                        <p className="text-[11px] text-[#5C5C59] mb-1.5">Welche Tische behalten? (nicht gewählte werden frei)</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {comboTables.map(t => {
                            const sel = keepSel.includes(t.id);
                            return (
                              <button
                                key={t.id}
                                onClick={() => setKeepSel(prev => sel ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                                data-testid={`fp-popover-keep-${r.id}-${t.id}`}
                                aria-pressed={sel}
                                className={`text-xs px-2.5 py-1 rounded border transition ${sel ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#1C1C19] hover:border-[#1C1C19]'}`}
                              >
                                {sel ? '\u2713 ' : ''}Tisch {t.table_number}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { onReduceCombination(r.id, keepSel); setSplitFor(null); setKeepSel([]); onClose(); }}
                            disabled={keepSel.length === 0 || keepSel.length === comboTables.length}
                            data-testid={`fp-popover-keep-confirm-${r.id}`}
                            className="text-xs px-3 py-1.5 rounded bg-[#F97316] text-white hover:bg-[#EA580C] transition disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                          >
                            {keepSel.length <= 1 ? `Behalten & übrige freigeben` : `${keepSel.length} Tische behalten`}
                          </button>
                          <button onClick={() => { setSplitFor(null); setKeepSel([]); }} className="text-xs px-2 py-1 text-[#5C5C59] hover:text-[#1C1C19]">Abbrechen</button>
                        </div>
                        {keepSel.length === comboTables.length && (
                          <p className="text-[10px] text-[#9CA3AF] mt-1">Es müssen weniger Tische als die ganze Kombination gewählt sein.</p>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => { setSplitFor(r.id); setKeepSel([]); }}
                        data-testid={`fp-popover-split-${r.id}`}
                        className="text-xs text-[#F97316] hover:underline inline-flex items-center gap-1"
                      >
                        <Maximize2 className="w-3 h-3" /> Kombination auflösen (Tische freigeben)
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-2 p-3 border-t border-[#E5E5E0]">
          <button
            onClick={(state === 'free' || state === 'reserved') && onQuickWalkIn ? onQuickWalkIn : onAddWalkIn}
            disabled={blocked}
            data-testid="fp-popover-walkin"
            title={(state === 'free' || state === 'reserved') ? 'Sofort-Walk-in (2 Pers., ohne Dialog)' : 'Walk-in / Tisch teilen'}
            className="text-sm py-2 px-2 rounded-lg border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition"
          >
            <UserPlus className="w-4 h-4" /> {(state === 'free' || state === 'reserved') ? 'Sofort-Walk-in' : 'Walk-in'}
          </button>
          <button
            onClick={onAddReservation}
            disabled={blocked}
            data-testid="fp-popover-add"
            className="text-sm py-2 px-2 rounded-lg border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition"
          >
            <Plus className="w-4 h-4" /> Zusatz
          </button>
          {canSplitWalkin && onSplitWalkIn && (
            <button
              onClick={onSplitWalkIn}
              data-testid="fp-popover-split-walkin"
              title={`Tisch teilen – ${usedSeats}/${table.max_persons} belegt`}
              className="col-span-2 text-sm py-2 px-2 rounded-lg border border-[#0EA5E9] text-[#0369A1] bg-[#0EA5E9]/5 hover:bg-[#0EA5E9] hover:text-white flex items-center justify-center gap-1.5 transition"
            >
              <Users className="w-4 h-4" /> Tisch teilen – weiterer Walk-in ({usedSeats}/{table.max_persons})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function WaiterManagerDialog({ waiters, onClose, onCreate, onUpdate, onDelete }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(WAITER_PALETTE[0]);

  const add = () => {
    const n = newName.trim();
    if (!n) { toast.info('Bitte einen Namen eingeben'); return; }
    onCreate(n, newColor);
    setNewName('');
    setNewColor(WAITER_PALETTE[(waiters.length + 1) % WAITER_PALETTE.length]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="waiter-manager-dialog" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h3 className="font-display text-xl text-[#1C1C19]">Kellner verwalten</h3>
          <button onClick={onClose} data-testid="waiter-manager-close" className="text-[#5C5C59] hover:text-[#1C1C19] p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-2 max-h-[50vh] overflow-y-auto">
          {waiters.length === 0 && <p className="text-sm text-[#9CA3AF] italic">Noch keine Kellner angelegt.</p>}
          {waiters.map(w => (
            <WaiterRow key={w.id} waiter={w} onUpdate={onUpdate} onDelete={onDelete} />
          ))}
        </div>

        <div className="px-5 py-4 border-t border-[#E5E5E0] bg-[#F9F9F7]">
          <p className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-2">Neuer Kellner</p>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              data-testid="waiter-new-color"
              className="w-9 h-9 rounded border border-[#E5E5E0] cursor-pointer p-0.5 bg-white"
              title="Farbe"
            />
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
              placeholder="Name"
              data-testid="waiter-new-name"
              className="flex-1 px-3 py-2 rounded border border-[#E5E5E0] text-sm focus:outline-none focus:border-[#1C1C19]"
            />
            <button onClick={add} data-testid="waiter-add-btn" className="px-3 py-2 rounded text-sm bg-[#1C1C19] hover:bg-[#3A3A35] text-white flex items-center gap-1 transition">
              <Plus className="w-4 h-4" /> Anlegen
            </button>
          </div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {WAITER_PALETTE.map(c => (
              <button key={c} onClick={() => setNewColor(c)} className={`w-5 h-5 rounded-full border-2 ${newColor === c ? 'border-[#1C1C19]' : 'border-transparent'}`} style={{ background: c }} title={c} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function WaiterRow({ waiter, onUpdate, onDelete }) {
  const [name, setName] = useState(waiter.name);
  const [color, setColor] = useState(waiter.color);

  return (
    <div className="flex items-center gap-2 border border-[#E5E5E0] rounded p-2" data-testid={`waiter-row-${waiter.id}`}>
      <input
        type="color"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        onBlur={() => { if (color && color !== waiter.color) onUpdate(waiter.id, { color }); }}
        data-testid={`waiter-color-${waiter.id}`}
        className="w-8 h-8 rounded border border-[#E5E5E0] cursor-pointer p-0.5 bg-white"
        title="Farbe ändern"
      />
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => { if (name.trim() && name.trim() !== waiter.name) onUpdate(waiter.id, { name: name.trim() }); }}
        data-testid={`waiter-name-${waiter.id}`}
        className="flex-1 px-2 py-1.5 rounded border border-transparent hover:border-[#E5E5E0] focus:border-[#1C1C19] focus:outline-none text-sm"
      />
      <button
        onClick={() => onDelete(waiter.id)}
        data-testid={`waiter-delete-${waiter.id}`}
        className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded transition"
        title="Löschen"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}


function TableDayBlockDialog({ table, date, blocks, existingResvCount, onCreate, onDelete, onClose }) {
  const [mode, setMode] = useState('full');  // 'full' | 'timed'
  const [start, setStart] = useState('18:00');
  const [end, setEnd] = useState('20:00');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    if (mode === 'timed') {
      if (!start || !end) { toast.info('Bitte Start- und Endzeit angeben'); return; }
      if (start >= end) { toast.info('Startzeit muss vor Endzeit liegen'); return; }
    }
    setSubmitting(true);
    try {
      await onCreate({
        table_id: table.id,
        full_day: mode === 'full',
        start_time: mode === 'timed' ? start : null,
        end_time: mode === 'timed' ? end : null,
        note: note.trim(),
      });
      setNote('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="dayblock-dialog" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl text-[#1C1C19]">Tagessperre · Tisch {table.table_number}</h3>
            <p className="text-xs text-[#5C5C59] mt-0.5">{formatDate(date)}</p>
          </div>
          <button onClick={onClose} data-testid="dayblock-close" className="text-[#5C5C59] hover:text-[#1C1C19] p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Bestehende Sperren */}
          {blocks.length > 0 && (
            <div className="space-y-2" data-testid="dayblock-existing">
              <p className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Aktive Sperren an diesem Tag</p>
              {blocks.map(b => {
                const full = b.full_day || !b.start_time || !b.end_time;
                return (
                  <div key={b.id} className="flex items-center justify-between gap-2 border border-[#EF4444]/30 bg-[#EF4444]/5 rounded p-2" data-testid={`dayblock-item-${b.id}`}>
                    <div className="text-sm text-[#1C1C19]">
                      <span className="font-medium">{full ? 'Ganztags gesperrt' : `Gesperrt ${b.start_time}–${b.end_time}`}</span>
                      {b.note && <span className="text-xs text-[#5C5C59] block">{b.note}</span>}
                    </div>
                    <button
                      onClick={() => onDelete(b.id)}
                      data-testid={`dayblock-remove-${b.id}`}
                      className="px-2.5 py-1.5 rounded text-xs border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white flex items-center gap-1 transition"
                    >
                      <Unlock className="w-3.5 h-3.5" /> Aufheben
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Warnung bei bestehenden Reservierungen */}
          {existingResvCount > 0 && (
            <div className="px-3 py-2 rounded bg-[#F97316]/10 border border-[#F97316]/30 text-xs text-[#9A3412]" data-testid="dayblock-warning">
              Achtung: An diesem Tag bestehen bereits {existingResvCount} Reservierung(en) auf diesem Tisch. Die Sperre verhindert nur neue Buchungen – bestehende bleiben erhalten.
            </div>
          )}

          {/* Neue Sperre */}
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Neue Sperre hinzufügen</p>
            <div className="flex gap-2">
              <button
                onClick={() => setMode('full')}
                data-testid="dayblock-mode-full"
                className={`flex-1 px-3 py-2 rounded text-sm border transition ${mode === 'full' ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}
              >
                Ganzer Tag
              </button>
              <button
                onClick={() => setMode('timed')}
                data-testid="dayblock-mode-timed"
                className={`flex-1 px-3 py-2 rounded text-sm border transition ${mode === 'timed' ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}
              >
                Zeitfenster
              </button>
            </div>

            {mode === 'timed' && (
              <div className="flex items-center gap-2">
                <input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dayblock-start" className="flex-1 px-3 py-2 rounded border border-[#E5E5E0] text-sm focus:outline-none focus:border-[#1C1C19]" />
                <span className="text-[#5C5C59]">–</span>
                <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dayblock-end" className="flex-1 px-3 py-2 rounded border border-[#E5E5E0] text-sm focus:outline-none focus:border-[#1C1C19]" />
              </div>
            )}

            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notiz (optional, z. B. defekt oder privat reserviert)"
              data-testid="dayblock-note"
              className="w-full px-3 py-2 rounded border border-[#E5E5E0] text-sm focus:outline-none focus:border-[#1C1C19]"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#E5E5E0] flex justify-end gap-2">
          <button onClick={onClose} data-testid="dayblock-cancel" className="px-4 py-2 text-sm border border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19] hover:text-[#1C1C19] rounded transition">
            Schließen
          </button>
          <button onClick={submit} disabled={submitting} data-testid="dayblock-submit" className="px-4 py-2 text-sm bg-[#EF4444] hover:bg-[#DC2626] text-white rounded flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition">
            <Lock className="w-4 h-4" /> Sperren
          </button>
        </div>
      </div>
    </div>
  );
}

